<template>
  <div class="assistente-projeto" :class="{ bloqueado: salvandoEtapa }">
    <div
      v-if="salvandoEtapa"
      class="overlay-salvando"
      role="alert"
      aria-live="assertive"
    >
      <div class="overlay-conteudo">
        <span class="spinner" aria-hidden="true"></span>
        <p>Salvando alterações...</p>
      </div>
    </div>
    <header>
      <h2>{{ tituloFormulario }}</h2>
      <p>Preencha as etapas para cadastrar o projeto completo.</p>
    </header>

    <div
      v-if="alerta.mensagem"
      class="alerta"
      :data-tipo="alerta.tipo"
      role="status"
      aria-live="polite"
    >
      {{ alerta.mensagem }}
    </div>

    <nav class="etapas-container">
      <div class="etapas">
        <button
          v-for="(etapa, index) in etapas"
          :key="etapa.id"
          :class="{
            ativo: etapaAtual === etapa.id,
            concluida: etapaConcluida(etapa.id),
            'primeira-etapa': index === 0,
            'ultima-etapa': index === etapas.length - 1,
          }"
          type="button"
          @click="navegarParaEtapa(etapa.id)"
        >
          <div class="etapa-numero">
            <span
              v-if="etapaConcluida(etapa.id) && etapaAtual !== etapa.id"
              class="check-icon"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                />
              </svg>
            </span>
            <span v-else class="numero">{{ index + 1 }}</span>
          </div>
          <div class="etapa-content">
            <span class="etapa-nome">{{ etapa.nome }}</span>
            <span
              class="etapa-status-texto"
              v-if="etapaConcluida(etapa.id) && etapaAtual !== etapa.id"
            >
              Concluída
            </span>
            <span
              class="etapa-status-texto"
              v-else-if="etapaAtual === etapa.id"
            >
              Em andamento
            </span>
            <span class="etapa-status-texto" v-else> Pendente </span>
          </div>
        </button>
      </div>
    </nav>

    <form
      ref="formulario"
      @submit.prevent="salvar"
      @blur.capture="agendarAutosaveCampoProjeto"
      @change.capture="agendarAutosaveCampoProjeto"
    >
      <section v-if="etapaAtual === 'basico'" class="secao-dados-basicos">
        <div class="secao-header">
          <div class="header-content">
            <h3>Dados básicos do projeto</h3>
            <p>
              Preencha as informações principais para identificar e
              contextualizar o projeto
            </p>
          </div>
        </div>

        <div class="form-content">
          <div class="campo-principal">
            <label :class="['campo-titulo', classeObrigatorio(form.titulo)]">
              <span class="label-text">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                  />
                  <polyline points="14,2 14,8 20,8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10,9 9,9 8,9" />
                </svg>
                Título do projeto *
              </span>
              <input
                v-model="form.titulo"
                data-autosave-campo="titulo"
                type="text"
                required
                placeholder="Ex: Plano de turismo municipal"
                :class="{ invalido: erros.titulo }"
                class="input-principal"
                @blur="validarTitulo"
              />
              <div v-if="erros.titulo" class="campo-erro">
                {{ erros.titulo }}
              </div>
            </label>
          </div>

          <div class="campos-grid">
            <label class="campo">
              <span class="label-text">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                Município
              </span>
              <div class="input-wrapper">
                <ComboboxMunicipio
                  ref="municipioCombobox"
                  :filter="municipioFiltro"
                  class="combobox-municipio"
                  clearable
                  @changeCity="aoAlterarMunicipio"
                />
              </div>
            </label>

            <label class="campo">
              <span class="label-text">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M9 12l2 2 4-4" />
                  <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3" />
                  <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3" />
                  <path d="M12 3c0 1-1 3-3 3s-3-2-3-3 1-3 3-3 3 2 3 3" />
                  <path d="M12 21c0-1 1-3 3-3s3 2 3 3-1 3-3 3-3-2-3-3" />
                </svg>
                Plano Municipal (PDM)
              </span>
              <select
                v-model="form.idPdm"
                data-autosave-campo="idPdm"
                :disabled="carregandoPdms || !pdmsDisponiveis.length"
                class="select-field"
              >
                <option :value="null">Selecione</option>
                <option
                  v-for="pdm in pdmsDisponiveis"
                  :key="identificarPdm(pdm)"
                  :value="identificarPdm(pdm)"
                >
                  {{ pdm.nome || pdm.titulo || pdm.descricao || "PDM" }}
                </option>
              </select>
              <small v-if="carregandoPdms" class="campo-ajuda loading">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  class="spin"
                >
                  <path
                    d="M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,19a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z"
                    opacity=".25"
                  />
                  <path
                    d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z"
                  />
                </svg>
                Carregando planos municipais...
              </small>
              <small
                v-else-if="form.idMunicipio && !pdmsDisponiveis.length"
                class="campo-ajuda warning"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path
                    d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"
                  />
                </svg>
                Nenhum PDM cadastrado para o município selecionado.
              </small>
            </label>
          </div>

          <label class="campo">
            <span class="label-text">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                />
                <polyline points="14,2 14,8 20,8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
              Descrição
            </span>
            <textarea
              v-model="form.descricao"
              data-autosave-campo="descricao"
              rows="4"
              placeholder="Descreva o objetivo e escopo do projeto..."
              class="textarea-field"
            ></textarea>
          </label>

          <label class="campo">
            <span class="label-text">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
              </svg>
              Áreas Chave da Gestão Municipal
            </span>
            <select
              v-model="form.idsEixosPdm"
              multiple
              :disabled="carregandoEixosPdm || !eixosPdmDisponiveis.length"
              class="select-field select-multiple"
            >
              <option v-if="!form.idPdm" disabled value="">
                Selecione um PDM primeiro
              </option>
              <option
                v-for="eixo in eixosPdmDisponiveis"
                :key="identificarEixo(eixo)"
                :value="identificarEixo(eixo)"
              >
                {{ eixo.nome || eixo.descricao || eixo.titulo || "Eixo" }}
              </option>
            </select>
            <small v-if="carregandoEixosPdm" class="campo-ajuda loading">
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="currentColor"
                class="spin"
              >
                <path
                  d="M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,19a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z"
                  opacity=".25"
                />
                <path
                  d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z"
                />
              </svg>
              Carregando áreas chave...
            </small>
            <small
              v-else-if="form.idPdm && !eixosPdmDisponiveis.length"
              class="campo-ajuda warning"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
              </svg>
              Nenhuma área chave cadastrada para o PDM selecionado.
            </small>
          </label>

          <div class="campos-grid">
            <label class="campo">
              <span class="label-text">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M8 2v4m8-4v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"
                  />
                </svg>
                Data de início
              </span>
              <div class="input-wrapper-date">
                <input
                  v-model="datas.dataInicio"
                  data-autosave-campo="dataInicio"
                  type="text"
                  placeholder="DD/MM/AAAA"
                  maxlength="10"
                  :class="{
                    invalido: erros.dataInicio || erros.periodoPrincipal,
                  }"
                  class="input-field date-field"
                  @input="manipularEntradaDataComMascara($event, 'dataInicio')"
                  @blur="normalizarData('dataInicio')"
                />
                <input
                  ref="dataInicioNative"
                  data-autosave-campo="dataInicio"
                  type="date"
                  class="date-picker-hidden"
                  @change="aoSelecionarDataCalendario($event, 'dataInicio')"
                />
                <button
                  type="button"
                  class="calendar-icon"
                  @click="$refs.dataInicioNative.showPicker()"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </button>
              </div>
            </label>

            <label class="campo">
              <span class="label-text">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M8 2v4m8-4v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"
                  />
                  <circle cx="12" cy="16" r="2" />
                </svg>
                Data de fim
              </span>
              <div class="input-wrapper-date">
                <input
                  v-model="datas.dataFim"
                  data-autosave-campo="dataFim"
                  type="text"
                  placeholder="DD/MM/AAAA"
                  maxlength="10"
                  :class="{ invalido: erros.dataFim || erros.periodoPrincipal }"
                  class="input-field date-field"
                  @input="manipularEntradaDataComMascara($event, 'dataFim')"
                  @blur="normalizarData('dataFim')"
                />
                <input
                  ref="dataFimNative"
                  data-autosave-campo="dataFim"
                  type="date"
                  class="date-picker-hidden"
                  @change="aoSelecionarDataCalendario($event, 'dataFim')"
                />
                <button
                  type="button"
                  class="calendar-icon"
                  @click="$refs.dataFimNative.showPicker()"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </button>
              </div>
              <div v-if="erros.dataFim" class="campo-erro">
                {{ erros.dataFim }}
              </div>
            </label>
          </div>

          <div class="campos-grid">
            <label class="campo">
              <span class="label-text">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <circle cx="12" cy="12" r="3" />
                  <path
                    d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2 2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"
                  />
                </svg>
                Situação
              </span>
              <select
                v-model="form.situacao"
                data-autosave-campo="situacao"
                class="select-field"
              >
                <option
                  v-for="situacao in opcoes.situacoes"
                  :key="situacao"
                  :value="situacao"
                >
                  {{ situacao }}
                </option>
              </select>
            </label>

            <label class="campo">
              <span class="label-text">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <polygon
                    points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                  />
                </svg>
                Prioridade
              </span>
              <select
                v-model="form.prioridade"
                data-autosave-campo="prioridade"
                class="select-field"
              >
                <option
                  v-for="prioridade in opcoes.prioridades"
                  :key="prioridade"
                  :value="prioridade"
                >
                  {{ prioridade }}
                </option>
              </select>
            </label>
          </div>

          <label class="campo">
            <span class="label-text">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
              </svg>
              Saúde
            </span>
            <select
              v-model="form.nivelSaude"
              data-autosave-campo="nivelSaude"
              class="select-field"
            >
              <option
                v-for="nivel in opcoes.niveisSaude"
                :key="nivel"
                :value="nivel"
              >
                {{ nivel }}
              </option>
            </select>
          </label>
        </div>
      </section>
      <section v-if="etapaAtual === 'acoes'" class="secao-dados-acoes">
        <div class="secao-header">
          <div class="header-content">
            <h3>Ações</h3>
          </div>
          <div class="acoes">
            <button
              v-if="emEdicao"
              type="button"
              class="secundario"
              @click="alternarLixeiraAcoes"
            >
              {{ lixeiraAberta ? "Ocultar lixeira" : "Lixeira de ações" }}
            </button>
            <button type="button" class="botao-adicionar" @click="adicionarAcao">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Adicionar ação
            </button>
          </div>
        </div>
        <div v-if="lixeiraAberta" class="guia-estrutura">
          <h4>Lixeira de ações</h4>
          <p v-if="carregandoLixeira">Carregando ações excluídas...</p>
          <p v-else-if="!acoesExcluidas.length">Nenhuma ação na lixeira.</p>
          <ul v-else>
            <li v-for="acaoExcluida in acoesExcluidas" :key="acaoExcluida.idMarco">
              <span>{{ acaoExcluida.titulo || "Ação sem título" }}</span>
              <button
                type="button"
                class="secundario"
                @click="restaurarAcaoExcluida(acaoExcluida)"
              >
                Restaurar
              </button>
            </li>
          </ul>
        </div>
        <div class="guia-estrutura">
          <h4>Entenda a hierarquia do projeto</h4>
          <p>
            Use os níveis abaixo para estruturar o plano de trabalho e deixe
            visível o que cada pessoa precisa acompanhar.
          </p>
          <ul>
            <li>
              <span class="badge badge-acao">Ação</span>
              Macro iniciativa do projeto. Agrupa entregas relacionadas e indica
              o objetivo principal.
            </li>
            <li>
              <span class="badge badge-entrega">Entrega</span>
              Resultado concreto que comprova o avanço da ação. Pode ter
              responsável, prazo, peso e progresso.
            </li>
            <li>
              <span class="badge badge-tarefa">Tarefa</span>
              Atividade específica necessária para concluir uma entrega.
              Normalmente é a menor unidade acompanhada.
            </li>
          </ul>
        </div>

        <div v-if="errosPesos.length" class="alerta" data-tipo="erro">
          <p v-for="(mensagem, indice) in errosPesos" :key="indice">
            {{ mensagem }}
          </p>
        </div>
        <article v-if="!form.acoes.length" class="vazio">
          Nenhuma ação cadastrada.
        </article>
        <article
          v-for="(acao, indiceAcao) in form.acoes"
          :key="acao.__id"
          class="cartao acao"
          :class="{ colapsado: acao.__colapsada }"
        >
          <header class="cartao-cabecalho acao">
            <div class="cartao-identificacao">
              <span class="badge badge-acao">Ação</span>
              <div class="cartao-textos">
                <strong>{{ acao.titulo || "Ação sem título" }}</strong>
                <small>
                  {{ resumoPeriodoAcao(acao) }} • Situação:
                  {{ acao.situacao || "—" }} • Progresso:
                  {{ formatarPercentual(acao.progresso) }}
                </small>
              </div>
            </div>
            <button
              type="button"
              class="alternar-colapso"
              @click="alternarDetalhesAcao(acao)"
            >
              {{ acao.__colapsada ? "Expandir detalhes" : "Recolher detalhes" }}
            </button>
          </header>
          <div v-show="!acao.__colapsada" class="cartao-conteudo">
            <div class="campos-grid">
              <label class="campo">
                <span class="label-text">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                    />
                    <polyline points="14,2 14,8 20,8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10,9 9,9 8,9" />
                  </svg>
                  Título da ação *
                </span>
                <input
                  v-model="acao.titulo"
                  required
                  placeholder="Ex: Implementar sistema de gestão digital"
                  class="input-field"
                />
              </label>

              <div class="campo-grupo">
                <label class="campo">
                  <span class="label-text">Início planejado</span>
                  <input
                    :value="converterDataParaIso(acao.dataInicio)"
                    type="date"
                    class="input-field"
                    @input="
                      atualizarDataAcao(acao, 'dataInicio', $event.target.value)
                    "
                  />
                </label>

                <label class="campo">
                  <span class="label-text">Fim planejado</span>
                  <input
                    :value="converterDataParaIso(acao.dataFim)"
                    type="date"
                    class="input-field"
                    @input="
                      atualizarDataAcao(acao, 'dataFim', $event.target.value)
                    "
                  />
                </label>
              </div>

              <label class="campo">
                <span class="label-text">Data limite</span>
                <input
                  :value="converterDataParaIso(acao.dataLimite)"
                  type="date"
                  class="input-field"
                  @input="
                    atualizarDataAcao(acao, 'dataLimite', $event.target.value)
                  "
                />
              </label>

              <label class="campo">
                <span class="label-text">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <polyline points="9,11 12,14 22,4" />
                    <path
                      d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
                    />
                  </svg>
                  Situação
                </span>
                <select v-model="acao.situacao" class="select-field">
                  <option value="">Selecione a situação</option>
                  <option
                    v-for="situacao in opcoes.situacoes"
                    :key="situacao"
                    :value="situacao"
                  >
                    {{ situacao }}
                  </option>
                </select>
              </label>

              <label class="campo">
                <span class="label-text">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <circle cx="12" cy="12" r="6" />
                    <circle cx="12" cy="12" r="2" />
                  </svg>
                  Progresso (%)
                </span>
                <div class="campo-com-acao">
                  <input
                    v-model.number="acao.progresso"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder="0"
                    class="input-field"
                    @input="marcarProgressoAcaoManual(acao)"
                    @blur="limitarPercentual(acao, 'progresso')"
                  />
                  <button
                    type="button"
                    class="acao-texto"
                    :disabled="!acao.entregas.length"
                    @click="restaurarProgressoAcao(acao)"
                  >
                    Recalcular
                  </button>
                </div>
              </label>
            </div>

            <label class="campo coluna-cheia">
              <span class="label-text">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                  />
                  <polyline points="14,2 14,8 20,8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10,9 9,9 8,9" />
                </svg>
                Descrição
              </span>
              <textarea
                v-model="acao.descricao"
                rows="3"
                placeholder="Descreva o objetivo e escopo desta ação"
                class="textarea-field"
              ></textarea>
            </label>
            <label>
              Métricas vinculadas
              <select
                v-model="acao.metricasVinculadas"
                multiple
                :disabled="!form.metricas.length"
                @focus="prepararAlteracaoMetricasAcao(acao)"
                @mousedown="prepararAlteracaoMetricasAcao(acao)"
                @change="salvarMetricasVinculadasAcao(acao)"
              >
                <option
                  v-for="metrica in form.metricas"
                  :key="identificadorMetrica(metrica)"
                  :value="identificadorMetrica(metrica)"
                >
                  {{ metrica.nome }}
                </option>
              </select>
              <small v-if="!form.metricas.length" class="ajuda"
                >Cadastre métricas na etapa específica para vincular
                aqui.</small
              >
            </label>
            <header class="secao-cabecalho interno">
              <h4>Entregas vinculadas</h4>
              <button type="button" @click="adicionarEntrega(indiceAcao)">
                Adicionar entrega
              </button>
            </header>
            <article v-if="!acao.entregas.length" class="vazio interno">
              Nenhuma entrega cadastrada.
            </article>
            <article
              v-for="(entrega, indiceEntrega) in acao.entregas"
              :key="entrega.__id"
              class="cartao interno entrega"
              :class="{ colapsado: entrega.__colapsada }"
            >
              <header class="cartao-cabecalho entrega">
                <div class="cartao-identificacao">
                  <span class="badge badge-entrega">Entrega</span>
                  <div class="cartao-textos">
                    <strong>{{
                      entrega.titulo || "Entrega sem título"
                    }}</strong>
                    <small>
                      {{ resumoPrazoEntrega(entrega) }} • Situação:
                      {{ entrega.situacao || "—" }} • Progresso:
                      {{ formatarPercentual(entrega.percentualConclusao) }}
                    </small>
                  </div>
                </div>
                <button
                  type="button"
                  class="alternar-colapso"
                  @click="alternarDetalhesEntrega(entrega)"
                >
                  {{
                    entrega.__colapsada
                      ? "Expandir detalhes"
                      : "Recolher detalhes"
                  }}
                </button>
              </header>
              <div v-show="!entrega.__colapsada" class="cartao-conteudo">
                <div class="campos-grid">
                  <label class="campo">
                    <span class="label-text">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <path
                          d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                        />
                        <polyline points="14,2 14,8 20,8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                        <polyline points="10,9 9,9 8,9" />
                      </svg>
                      Título da entrega *
                    </span>
                    <input
                      v-model="entrega.titulo"
                      required
                      placeholder="Ex: Relatório de diagnóstico municipal"
                      class="input-field"
                    />
                  </label>

                  <label class="campo">
                    <span class="label-text">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                      Responsável
                    </span>
                    <div class="input-wrapper">
                      <BuscaUsuario
                        v-model="entrega.idUsuarioResponsavel"
                        :descricao="entrega.responsavelNome"
                        @usuario-selecionado="
                          definirResponsavelEntrega(
                            indiceAcao,
                            indiceEntrega,
                            $event
                          )
                        "
                      />
                    </div>
                  </label>

                  <label class="campo">
                    <span class="label-text">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <path
                          d="M8 2v4m8-4v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"
                        />
                        <circle cx="12" cy="16" r="2" />
                      </svg>
                      Data limite
                    </span>
                    <input
                      v-model="entrega.dataLimite"
                      type="text"
                      inputmode="numeric"
                      placeholder="DD/MM/AAAA"
                      class="input-field"
                      @blur="normalizarDataEntrega(entrega, 'dataLimite')"
                      @input="
                        manipularEntradaData($event, 'dataLimite', entrega)
                      "
                    />
                  </label>

                  <label class="campo">
                    <span class="label-text">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <polyline points="9,11 12,14 22,4" />
                        <path
                          d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
                        />
                      </svg>
                      Situação
                    </span>
                    <select v-model="entrega.situacao" class="select-field">
                      <option value="">Selecione a situação</option>
                      <option
                        v-for="situacao in opcoes.situacoes"
                        :key="situacao"
                        :value="situacao"
                      >
                        {{ situacao }}
                      </option>
                    </select>
                  </label>

                  <label class="campo">
                    <span class="label-text">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <circle cx="12" cy="12" r="6" />
                        <circle cx="12" cy="12" r="2" />
                      </svg>
                      Progresso (%)
                    </span>
                    <div class="campo-com-acao">
                      <input
                        v-model.number="entrega.percentualConclusao"
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        placeholder="0"
                        class="input-field"
                        @input="marcarProgressoEntregaManual(entrega, acao)"
                        @blur="
                          limitarPercentual(entrega, 'percentualConclusao')
                        "
                      />
                      <button
                        type="button"
                        class="acao-texto"
                        :disabled="!entrega.tarefas.length"
                        @click="restaurarProgressoEntrega(acao, entrega)"
                      >
                        Recalcular
                      </button>
                    </div>
                  </label>

                  <label class="campo">
                    <span class="label-text">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <path
                          d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                        />
                        <polyline points="14,2 14,8 20,8" />
                        <path d="M9 9h1m0 0h1" />
                      </svg>
                      Peso (%)
                    </span>
                    <input
                      v-model.number="entrega.peso"
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      placeholder="0"
                      class="input-field"
                      @input="aoAlterarEntrega(indiceAcao, indiceEntrega)"
                      @blur="aoAlterarEntrega(indiceAcao, indiceEntrega)"
                    />
                  </label>
                </div>

                <label class="campo coluna-cheia">
                  <span class="label-text">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                      />
                      <polyline points="14,2 14,8 20,8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                      <polyline points="10,9 9,9 8,9" />
                    </svg>
                    Descrição
                  </span>
                  <textarea
                    v-model="entrega.descricao"
                    rows="3"
                    placeholder="Descreva o resultado esperado desta entrega"
                    class="textarea-field"
                  ></textarea>
                </label>
                <header class="secao-cabecalho interno">
                  <h5>Tarefas</h5>
                  <button
                    type="button"
                    @click="adicionarTarefa(indiceAcao, indiceEntrega)"
                  >
                    Adicionar tarefa
                  </button>
                </header>
                <article v-if="!entrega.tarefas.length" class="vazio interno">
                  Nenhuma tarefa cadastrada.
                </article>
                <article
                  v-for="(tarefa, indiceTarefa) in entrega.tarefas"
                  :key="tarefa.__id"
                  class="cartao tarefa"
                >
                  <div class="cartao-identificacao tarefa">
                    <span class="badge badge-tarefa">Tarefa</span>
                    <div class="cartao-textos">
                      <strong>{{
                        tarefa.titulo || "Tarefa sem título"
                      }}</strong>
                      <small>
                        {{ resumoPrazoTarefa(tarefa) }} • Situação:
                        {{ tarefa.situacao || "—" }} • Progresso:
                        {{ formatarPercentual(tarefa.percentualConclusao) }}
                      </small>
                    </div>
                  </div>
                  <div class="campos-grid">
                    <label class="campo">
                      <span class="label-text">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                        >
                          <path d="M9 12l2 2 4-4" />
                          <path
                            d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3"
                          />
                          <path
                            d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3"
                          />
                        </svg>
                        Título da tarefa *
                      </span>
                      <input
                        v-model="tarefa.titulo"
                        required
                        placeholder="Ex: Aplicar questionários de pesquisa"
                        class="input-field"
                      />
                    </label>

                    <label class="campo">
                      <span class="label-text">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                        >
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                        Responsável
                      </span>
                      <div class="input-wrapper">
                        <BuscaUsuario
                          v-model="tarefa.idUsuarioResponsavel"
                          :descricao="tarefa.responsavelNome"
                          @usuario-selecionado="
                            definirResponsavelTarefa(
                              indiceAcao,
                              indiceEntrega,
                              indiceTarefa,
                              $event
                            )
                          "
                        />
                      </div>
                    </label>

                    <label class="campo">
                      <span class="label-text">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                        >
                          <path
                            d="M8 2v4m8-4v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"
                          />
                          <circle cx="12" cy="16" r="2" />
                        </svg>
                        Data limite
                      </span>
                      <input
                        v-model="tarefa.dataLimite"
                        type="text"
                        inputmode="numeric"
                        placeholder="DD/MM/AAAA"
                        class="input-field"
                        @blur="normalizarDataTarefa(tarefa, 'dataLimite')"
                        @input="
                          manipularEntradaData($event, 'dataLimite', tarefa)
                        "
                      />
                    </label>

                    <label class="campo">
                      <span class="label-text">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                        >
                          <polyline points="9,11 12,14 22,4" />
                          <path
                            d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
                          />
                        </svg>
                        Situação
                      </span>
                      <select v-model="tarefa.situacao" class="select-field">
                        <option value="Planejamento">Planejamento</option>
                        <option value="Execucao">Execução</option>
                        <option value="Concluida">Concluída</option>
                        <option value="Bloqueada">Bloqueada</option>
                      </select>
                    </label>

                    <label class="campo">
                      <span class="label-text">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <circle cx="12" cy="12" r="6" />
                          <circle cx="12" cy="12" r="2" />
                        </svg>
                        Progresso (%)
                      </span>
                      <input
                        v-model.number="tarefa.percentualConclusao"
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        placeholder="0"
                        class="input-field"
                        @input="aoAlterarTarefa(indiceAcao, indiceEntrega)"
                        @blur="
                          limitarPercentual(tarefa, 'percentualConclusao');
                          aoAlterarTarefa(indiceAcao, indiceEntrega);
                        "
                      />
                    </label>

                    <label class="campo">
                      <span class="label-text">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                        >
                          <path
                            d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                          />
                          <polyline points="14,2 14,8 20,8" />
                          <path d="M9 9h1m0 0h1" />
                        </svg>
                        Peso (%)
                      </span>
                      <input
                        v-model.number="tarefa.peso"
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        placeholder="0"
                        class="input-field"
                        @input="aoAlterarTarefa(indiceAcao, indiceEntrega)"
                        @blur="aoAlterarTarefa(indiceAcao, indiceEntrega)"
                      />
                    </label>
                  </div>

                  <label class="campo coluna-cheia">
                    <span class="label-text">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <path
                          d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                        />
                        <polyline points="14,2 14,8 20,8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                        <polyline points="10,9 9,9 8,9" />
                      </svg>
                      Descrição
                    </span>
                    <textarea
                      v-model="tarefa.descricao"
                      rows="3"
                      placeholder="Descreva como realizar esta tarefa"
                      class="textarea-field"
                    ></textarea>
                  </label>
                  <button
                    type="button"
                    class="perigo"
                    @click="
                      removerTarefa(indiceAcao, indiceEntrega, indiceTarefa)
                    "
                  >
                    Remover tarefa
                  </button>
                </article>
                <button
                  type="button"
                  class="perigo"
                  @click="removerEntrega(indiceAcao, indiceEntrega)"
                >
                  Remover entrega
                </button>
              </div>
            </article>
            <button
              type="button"
              class="perigo"
              @click="removerAcao(indiceAcao)"
            >
              Remover ação
            </button>
          </div>
        </article>
      </section>

      <section v-if="etapaAtual === 'recursos'" class="secao">
        <div class="secao-container">
          <div class="secao-header">
            <div class="header-content">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <h3>Equipe do projeto</h3>
            </div>
            <button
              type="button"
              class="botao-adicionar"
              @click="adicionarMembro"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="16" />
                <line x1="8" y1="12" x2="16" y2="12" />
              </svg>
              Adicionar membro
            </button>
          </div>

          <div v-if="!form.equipes.length" class="estado-vazio">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <p>Nenhum membro da equipe cadastrado</p>
            <span>Adicione membros para formar a equipe do projeto</span>
          </div>

          <div class="membros-lista">
            <div
              v-for="(membro, indice) in form.equipes"
              :key="membro.__id"
              class="membro-item"
            >
              <div class="campos-grid">
                <label class="campo">
                  <span class="label-text">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    Usuário responsável
                  </span>
                  <div class="input-wrapper">
                    <BuscaUsuario
                      v-model="membro.idUsuario"
                      :descricao="membro.nomeUsuario"
                      @usuario-selecionado="definirMembro(indice, $event)"
                    />
                  </div>
                </label>
                <label class="campo">
                  <span class="label-text">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"
                      />
                      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                    </svg>
                    Papel
                  </span>
                  <input
                    v-model="membro.papel"
                    placeholder="Ex: Gestor municipal"
                    class="input-field"
                  />
                </label>
                <label class="campo">
                  <span class="label-text">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <circle cx="12" cy="12" r="3" />
                      <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1" />
                    </svg>
                    Alocação (%)
                  </span>
                  <input
                    v-model.number="membro.alocacaoPercentual"
                    type="number"
                    min="0"
                    max="100"
                    placeholder="0"
                    class="input-field"
                  />
                </label>
              </div>

              <div class="campos-grid">
                <label class="campo">
                  <span class="label-text">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                      />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                    E-mail
                  </span>
                  <input
                    v-model="membro.emailUsuario"
                    type="email"
                    placeholder="email@exemplo.com"
                    class="input-field"
                  />
                </label>
                <label class="campo">
                  <span class="label-text">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
                      />
                    </svg>
                    Telefone
                  </span>
                  <input
                    :value="membro.telefoneUsuario"
                    type="tel"
                    placeholder="(00) 00000-0000"
                    class="input-field"
                    @input="atualizarTelefone($event, membro)"
                  />
                </label>
                <label class="campo">
                  <span class="label-text">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"
                      />
                      <polyline points="9,22 9,12 15,12 15,22" />
                    </svg>
                    Instituição
                  </span>
                  <div class="input-wrapper">
                    <BuscaInstituicao
                      v-model="membro.idInstituicao"
                      :descricao="membro.instituicaoNome"
                      placeholder="Buscar ou criar instituição"
                      @instituicao-selecionada="
                        definirInstituicaoMembro(indice, $event)
                      "
                    />
                  </div>
                </label>
              </div>

              <label class="campo">
                <span class="label-text">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"
                    />
                    <polyline points="14,2 14,8 20,8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <line x1="10" y1="9" x2="8" y2="9" />
                  </svg>
                  Responsabilidades
                </span>
                <textarea
                  v-model="membro.responsabilidades"
                  rows="3"
                  placeholder="Descreva as principais responsabilidades..."
                  class="input-field textarea-field"
                ></textarea>
              </label>

              <div class="item-actions">
                <button
                  type="button"
                  class="botao-remover"
                  @click="removerMembro(indice)"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <polyline points="3,6 5,6 21,6" />
                    <path
                      d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6"
                    />
                    <line x1="10" y1="11" x2="10" y2="17" />
                    <line x1="14" y1="11" x2="14" y2="17" />
                  </svg>
                  Remover membro
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="secao-container">
          <div class="secao-header">
            <div class="header-content">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
              <h3>Orçamento detalhado</h3>
            </div>
            <button
              type="button"
              class="botao-adicionar"
              @click="adicionarOrcamento"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="16" />
                <line x1="8" y1="12" x2="16" y2="12" />
              </svg>
              Adicionar categoria
            </button>
          </div>

          <div v-if="!form.orcamentos.length" class="estado-vazio">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1"
            >
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
            <p>Nenhuma categoria de orçamento registrada</p>
            <span
              >Adicione categorias para detalhar o orçamento do projeto</span
            >
          </div>

          <div class="orcamentos-lista">
            <div
              v-for="(orcamento, indice) in form.orcamentos"
              :key="orcamento.__id"
              class="orcamento-item"
            >
              <div class="campos-grid">
                <label class="campo">
                  <span class="label-text">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        d="M9 11H5a2 2 0 0 0-2 2v3c0 1.1.9 2 2 2h4m6-6h4a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-4m-6 0a2 2 0 0 0-2-2v-3a2 2 0 0 1 2-2m6 0a2 2 0 0 1 2-2v-3a2 2 0 0 0-2-2m-3 8.5h.01M12 13.5h.01M12 11h.01"
                      />
                    </svg>
                    Categoria
                  </span>
                  <input
                    v-model="orcamento.categoria"
                    placeholder="Ex: Consultorias"
                    class="input-field"
                  />
                </label>
                <label class="campo">
                  <span class="label-text">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <line x1="12" y1="1" x2="12" y2="23" />
                      <path
                        d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"
                      />
                    </svg>
                    Planejado (R$)
                  </span>
                  <input
                    :value="obterValorMoeda(orcamento, 'valorPlanejado')"
                    inputmode="decimal"
                    placeholder="R$ 0,00"
                    class="input-field"
                    @focus="
                      iniciarEdicaoMoeda($event, 'valorPlanejado', orcamento)
                    "
                    @input="
                      manipularEntradaMoeda($event, 'valorPlanejado', orcamento)
                    "
                    @blur="
                      finalizarEdicaoMoeda($event, 'valorPlanejado', orcamento)
                    "
                  />
                </label>
                <label class="campo">
                  <span class="label-text">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"
                      />
                      <circle cx="18" cy="5" r="3" />
                    </svg>
                    Executado (R$)
                  </span>
                  <input
                    :value="obterValorMoeda(orcamento, 'valorExecutado')"
                    inputmode="decimal"
                    placeholder="R$ 0,00"
                    class="input-field"
                    @focus="
                      iniciarEdicaoMoeda($event, 'valorExecutado', orcamento)
                    "
                    @input="
                      manipularEntradaMoeda($event, 'valorExecutado', orcamento)
                    "
                    @blur="
                      finalizarEdicaoMoeda($event, 'valorExecutado', orcamento)
                    "
                  />
                </label>
              </div>

              <div class="item-actions">
                <button
                  type="button"
                  class="botao-remover"
                  @click="removerOrcamento(indice)"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <polyline points="3,6 5,6 21,6" />
                    <path
                      d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6"
                    />
                    <line x1="10" y1="11" x2="10" y2="17" />
                    <line x1="14" y1="11" x2="14" y2="17" />
                  </svg>
                  Remover categoria
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section v-if="etapaAtual === 'metricas'" class="secao-dados-metricas">
        <div class="secao-header">
          <div class="header-content">
            <h3>Métricas de acompanhamento</h3>
          </div>

          <button
            type="button"
            class="botao-adicionar"
            @click="adicionarMetrica"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Adicionar métrica
          </button>
        </div>
        <div>
          <p>
            Utilize os marcos de mensuração para diferenciar
            <strong>T0</strong> (linha de base), <strong>T1</strong> (valor
            atual) e <strong>T2</strong> (meta projetada). Registre também a
            periodicidade com que o indicador será acompanhado nas observações.
          </p>
        </div>

        <div class="form-content">
          <div v-if="!form.metricas.length" class="estado-vazio">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            <p>Nenhuma métrica cadastrada</p>
            <span
              >Adicione métricas para acompanhar o progresso do projeto</span
            >
          </div>

          <div v-else class="metricas-lista">
            <div
              v-for="(metrica, indice) in form.metricas"
              :key="metrica.__id"
              class="metrica-item"
            >
              <div class="campos-grid">
                <label class="campo">
                  <span class="label-text">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        d="M9 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2z"
                      />
                      <path
                        d="M19 11h-4a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2z"
                      />
                      <path d="M12 5V3" />
                      <path d="M18 5V3" />
                      <path d="M6 5V3" />
                    </svg>
                    Nome da métrica *
                  </span>
                  <input
                    v-model="metrica.nome"
                    required
                    placeholder="Ex: Taxa de satisfação dos munícipes"
                    class="input-field"
                  />
                </label>

                <label class="campo">
                  <span class="label-text">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7z"
                      />
                    </svg>
                    Tipo
                  </span>
                  <select v-model="metrica.tipo" class="select-field">
                    <option value="">Selecione o tipo</option>
                    <option value="KPI">KPI</option>
                    <option value="OKR">OKR</option>
                    <option value="Personalizado">Personalizado</option>
                  </select>
                </label>

                <label class="campo">
                  <span class="label-text">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M9 12l2 2 4-4" />
                      <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3" />
                      <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3" />
                    </svg>
                    Linha de base (T0)
                  </span>
                  <input
                    v-model.number="metrica.linhaBase"
                    type="number"
                    step="0.01"
                    placeholder="Valor inicial"
                    class="input-field"
                  />
                </label>

                <label class="campo">
                  <span class="label-text">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                    </svg>
                    Valor atual (T1)
                  </span>
                  <input
                    v-model.number="metrica.valorAtual"
                    type="number"
                    step="0.01"
                    placeholder="Situação atual"
                    class="input-field"
                  />
                </label>

                <label class="campo">
                  <span class="label-text">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <polyline points="22,12 18,12 15,21 9,3 6,12 2,12" />
                    </svg>
                    Meta (T2)
                  </span>
                  <input
                    v-model.number="metrica.meta"
                    type="number"
                    step="0.01"
                    placeholder="Objetivo a alcançar"
                    class="input-field"
                  />
                </label>
              </div>

              <label class="campo coluna-cheia">
                <span class="label-text">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                    />
                    <polyline points="14,2 14,8 20,8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10,9 9,9 8,9" />
                  </svg>
                  Observações
                </span>
                <textarea
                  v-model="metrica.descricao"
                  rows="3"
                  placeholder="Periodicidade de acompanhamento, metodologia de coleta, etc."
                  class="textarea-field"
                ></textarea>
              </label>

              <div class="item-actions">
                <button
                  type="button"
                  class="botao-remover"
                  @click="removerMetrica(indice)"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <polyline points="3,6 5,6 21,6" />
                    <path
                      d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                    />
                  </svg>
                  Remover métrica
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section v-if="etapaAtual === 'revisao'" class="secao-revisao">
        <div class="secao-header">
          <div class="header-content">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M9 12l2 2 4-4" />
              <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3" />
              <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3" />
              <path d="M12 3c0 1-1 3-3 3s-3-2-3-3 1-3 3-3 3 2 3 3" />
            </svg>
            <div>
              <h3>Revisão final</h3>
              <p>Confira os principais dados antes de salvar o projeto</p>
            </div>
          </div>
        </div>

        <div class="revisao-container">
          <!-- Dados Básicos -->
          <div class="revisao-card">
            <div class="card-header">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                />
                <polyline points="14,2 14,8 20,8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
              <h4>Dados Básicos</h4>
            </div>
            <div class="card-content">
              <div class="info-item">
                <span class="label">Título</span>
                <span class="value">{{ form.titulo || "Não informado" }}</span>
              </div>
              <div class="info-item">
                <span class="label">Município</span>
                <span class="value">{{ municipioSelecionado }}</span>
              </div>
              <div class="info-item">
                <span class="label">Período</span>
                <span class="value">
                  {{ datas.dataInicio || "—" }} até {{ datas.dataFim || "—" }}
                </span>
              </div>
              <div class="info-item">
                <span class="label">Situação</span>
                <span
                  class="value badge"
                  :class="`badge-${form.situacao?.toLowerCase()}`"
                >
                  {{ form.situacao || "Não informado" }}
                </span>
              </div>
              <div class="info-item">
                <span class="label">Prioridade</span>
                <span class="value">{{
                  form.prioridade || "Não informado"
                }}</span>
              </div>
            </div>
          </div>

          <!-- PDM e Eixos -->
          <div class="revisao-card">
            <div class="card-header">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
              </svg>
              <h4>PDM e Áreas Chave</h4>
            </div>
            <div class="card-content">
              <div class="info-item">
                <span class="label">Plano Municipal</span>
                <span class="value">{{ nomePdmSelecionado }}</span>
              </div>
              <div class="info-item">
                <span class="label">Eixos vinculados</span>
                <div class="value">
                  <div v-if="eixosSelecionados.length" class="tags-container">
                    <span
                      v-for="eixo in eixosSelecionados"
                      :key="eixo"
                      class="tag"
                    >
                      {{ eixo }}
                    </span>
                  </div>
                  <span v-else class="empty">Nenhuma área chave vinculada</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Equipe -->
          <div v-if="form.equipes.length" class="revisao-card">
            <div class="card-header">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <h4>Equipe ({{ form.equipes.length }} membros)</h4>
            </div>
            <div class="card-content">
              <div class="equipe-lista">
                <div
                  v-for="membro in form.equipes.slice(0, 3)"
                  :key="membro.__id"
                  class="membro-item"
                >
                  <div class="membro-info">
                    <strong>{{
                      membro.nomeUsuario || membro.emailUsuario || "Sem nome"
                    }}</strong>
                    <span>{{ membro.papel || "Papel não definido" }}</span>
                  </div>
                  <div class="membro-alocacao">
                    {{ membro.alocacaoPercentual || 0 }}%
                  </div>
                </div>
                <div v-if="form.equipes.length > 3" class="mais-membros">
                  +{{ form.equipes.length - 3 }} membro(s) adicional(is)
                </div>
              </div>
            </div>
          </div>

          <!-- Orçamento -->
          <div class="revisao-card">
            <div class="card-header">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
              <h4>Orçamento</h4>
            </div>
            <div class="card-content">
              <div class="orcamento-grid">
                <div class="orcamento-item">
                  <span class="label">Planejado</span>
                  <span class="value montante planejado">
                    {{ formatarValor(form.orcamentoPlanejado) }}
                  </span>
                </div>
                <div class="orcamento-item">
                  <span class="label">Executado</span>
                  <span class="value montante executado">
                    {{ formatarValor(form.orcamentoExecutado) }}
                  </span>
                </div>
                <div
                  v-if="form.orcamentos.length"
                  class="orcamento-item full-width"
                >
                  <span class="label"
                    >Categorias ({{ form.orcamentos.length }})</span
                  >
                  <div class="categorias-lista">
                    <span
                      v-for="orcamento in form.orcamentos.slice(0, 3)"
                      :key="orcamento.__id"
                      class="categoria-tag"
                    >
                      {{ orcamento.categoria }}
                    </span>
                    <span
                      v-if="form.orcamentos.length > 3"
                      class="mais-categorias"
                    >
                      +{{ form.orcamentos.length - 3 }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Métricas -->
          <div v-if="form.metricas.length" class="revisao-card">
            <div class="card-header">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <polyline points="22,12 18,12 15,21 9,3 6,12 2,12" />
              </svg>
              <h4>Métricas ({{ form.metricas.length }})</h4>
            </div>
            <div class="card-content">
              <div class="metricas-lista">
                <div
                  v-for="metrica in form.metricas.slice(0, 3)"
                  :key="metrica.__id"
                  class="metrica-item"
                >
                  <div class="metrica-info">
                    <strong>{{ metrica.nome || "Métrica sem nome" }}</strong>
                    <span class="badge badge-metrica">{{
                      metrica.tipo || "KPI"
                    }}</span>
                  </div>
                  <div class="metrica-valores">
                    <span>T0: {{ metrica.linhaBase || "—" }}</span>
                    <span>T1: {{ metrica.valorAtual || "—" }}</span>
                    <span>T2: {{ metrica.meta || "—" }}</span>
                  </div>
                </div>
                <div v-if="form.metricas.length > 3" class="mais-metricas">
                  +{{ form.metricas.length - 3 }} métrica(s) adicional(is)
                </div>
              </div>
            </div>
          </div>

          <!-- Estrutura do Projeto -->
          <div class="revisao-card estrutura-card">
            <div class="card-header">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M9 12l2 2 4-4" />
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
              <h4>Estrutura do Projeto</h4>
            </div>
            <div class="card-content">
              <div class="estrutura-grid">
                <div class="estrutura-item acao">
                  <div class="estrutura-icon">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M9 12l2 2 4-4" />
                      <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3" />
                    </svg>
                  </div>
                  <div class="estrutura-info">
                    <span class="numero">{{ form.acoes.length }}</span>
                    <span class="label">{{
                      form.acoes.length === 1 ? "Ação" : "Ações"
                    }}</span>
                  </div>
                </div>

                <div class="estrutura-item entrega">
                  <div class="estrutura-icon">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  </div>
                  <div class="estrutura-info">
                    <span class="numero">{{ totalEntregas }}</span>
                    <span class="label">{{
                      totalEntregas === 1 ? "Entrega" : "Entregas"
                    }}</span>
                  </div>
                </div>

                <div class="estrutura-item tarefa">
                  <div class="estrutura-icon">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <circle cx="12" cy="12" r="3" />
                      <path d="M12 1v6m0 6v6" />
                    </svg>
                  </div>
                  <div class="estrutura-info">
                    <span class="numero">{{ totalTarefas }}</span>
                    <span class="label">{{
                      totalTarefas === 1 ? "Tarefa" : "Tarefas"
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer ref="acoes" class="acoes">
        <button type="button" class="secundario" @click="voltar">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 18L9 12L15 6"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          Voltar
        </button>
        <button type="button" @click="avancar" v-if="etapaAtual !== 'revisao'">
          Avançar
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9 18L15 12L9 6"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <button type="submit" v-else :disabled="salvando">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20 6L9 17L4 12"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          {{
            salvando
              ? "Salvando..."
              : emEdicao
              ? "Atualizar projeto"
              : "Criar projeto"
          }}
        </button>
      </footer>
    </form>
  </div>
</template>

<script>
import ComboboxMunicipio from "@/modules/city/components/ComboboxSingle.vue";
import {
  buscarOpcoes,
  buscarProjeto,
  criarProjeto,
  atualizarProjeto,
  atualizarCampoProjeto,
  excluirAcaoProjeto,
  listarAcoesExcluidasProjeto,
  restaurarAcaoProjeto,
  definirVinculoMetricaAcao,
  buscarPdms,
  buscarPdmDetalhe,
} from "../services/api";
import BuscaUsuario from "./BuscaUsuario.vue";
import BuscaInstituicao from "./BuscaInstituicao.vue";
import { mapGetters } from "vuex";

const uid = () => Math.random().toString(36).slice(2);

export default {
  name: "AssistenteGestaoProjeto",
  components: {
    BuscaUsuario,
    BuscaInstituicao,
    ComboboxMunicipio,
  },
  data() {
    return {
      emEdicao: false,
      salvando: false,
      salvandoEtapa: false,
      etapaAtual: "basico",
      etapas: [
        { id: "basico", nome: "Dados básicos" },
        { id: "recursos", nome: "Equipe & orçamento" },
        { id: "metricas", nome: "Métricas" },
        { id: "acoes", nome: "Ações" },
        { id: "revisao", nome: "Revisão" },
      ],
      form: this.estadoInicial(),
      projetoPersistidoId: null,
      datas: {
        dataInicio: "",
        dataFim: "",
      },
      opcoes: {
        situacoes: [],
        prioridades: [],
        niveisSaude: [],
      },
      pdmsDisponiveis: [],
      carregandoPdms: false,
      eixosPdmDisponiveis: [],
      carregandoEixosPdm: false,
      municipioFiltro: {
        municipio: null,
      },
      municipioSelecionadoNome: "",
      carregamentoInicial: true,
      alterado: false,
      rascunhoTimeout: null,
      autosaveCampoTimeout: null,
      autosaveCampoExecutando: false,
      autosaveCampoPendente: false,
      camposAutosavePendentes: {},
      alteracoesNaoAutosalvas: false,
      versaoProjeto: null,
      quantidadeAcoesPersistidas: 0,
      lixeiraAberta: false,
      carregandoLixeira: false,
      acoesExcluidas: [],
      chaveRascunho: "gestao-projetos:assistente",
      alerta: {
        tipo: "",
        mensagem: "",
      },
      erros: {
        periodoPrincipal: false,
        dataInicio: "",
        dataFim: "",
        titulo: "",
      },
      errosPesos: [],
      valoresMoedaEmEdicao: {},
      metricasVinculosSalvando: {},
      alteracaoSilenciosaVinculoMetrica: false,
    };
  },
  computed: {
    ...mapGetters(["uf"]),
    indiceEtapa() {
      return this.etapas.findIndex((etapa) => etapa.id === this.etapaAtual);
    },
    municipioSelecionado() {
      return (
        this.municipioSelecionadoNome ||
        this.form.nomeMunicipio ||
        "Não informado"
      );
    },
    totalEntregas() {
      return this.form.acoes.reduce(
        (total, acao) => total + (acao.entregas?.length || 0),
        0
      );
    },
    totalTarefas() {
      return this.form.acoes.reduce((total, acao) => {
        return (
          total +
          (acao.entregas || []).reduce((subTotal, entrega) => {
            return subTotal + (entrega.tarefas?.length || 0);
          }, 0)
        );
      }, 0);
    },
    nomePdmSelecionado() {
      const idSelecionado = this.normalizarIdentificador(this.form.idPdm);
      if (idSelecionado !== null && idSelecionado !== undefined) {
        const encontrado = this.pdmsDisponiveis.find(
          (pdm) => this.identificarPdm(pdm) === idSelecionado
        );
        if (encontrado) {
          return (
            encontrado.nome ||
            encontrado.titulo ||
            encontrado.descricao ||
            "Plano Municipal"
          );
        }
      }
      return (
        this.form.nomePdm || this.form.nomePlanoMunicipal || "Não vinculado"
      );
    },
    eixosSelecionados() {
      if (
        !Array.isArray(this.form.idsEixosPdm) ||
        !this.form.idsEixosPdm.length
      )
        return [];
      const mapa = new Map(
        this.eixosPdmDisponiveis.map((eixo) => [
          this.identificarEixo(eixo),
          eixo,
        ])
      );
      return this.form.idsEixosPdm
        .map((id) => {
          const chave = this.normalizarIdentificador(id);
          const eixo = mapa.get(chave);
          return eixo?.nome || eixo?.descricao || eixo?.titulo || null;
        })
        .filter(Boolean);
    },
    deveAlertarSaida() {
      return this.alterado && !this.salvando && !this.salvandoEtapa;
    },
    tituloFormulario() {
      return this.emEdicao || this.projetoPersistidoId
        ? "Atualizar projeto"
        : "Novo projeto";
    },
    idProjetoAtivo() {
      const idRota = this.$route.params.idProjeto;
      if (idRota) {
        const convertido = Number(idRota);
        return Number.isNaN(convertido) ? null : convertido;
      }
      return this.projetoPersistidoId;
    },
  },
  async created() {
    await this.carregarOpcoes();
    const { idProjeto } = this.$route.params;
    if (idProjeto) {
      this.emEdicao = true;
      const convertido = Number(idProjeto);
      if (!Number.isNaN(convertido)) {
        this.projetoPersistidoId = convertido;
      }
      await this.carregarProjeto(idProjeto);
    } else {
      await this.carregarRascunho();
    }
    this.carregamentoInicial = false;
  },
  watch: {
    form: {
      deep: true,
      handler() {
        if (this.alteracaoSilenciosaVinculoMetrica) return;
        this.registrarAlteracao();
      },
    },
    "form.idMunicipio": {
      handler(novoValor) {
        this.$nextTick(() => this.tentarSincronizarMunicipio());
        if (this.carregamentoInicial) return;
        if (!novoValor) {
          this.pdmsDisponiveis = [];
          this.eixosPdmDisponiveis = [];
          this.form.idPdm = null;
          this.form.nomePdm = "";
          this.form.idsEixosPdm = [];
          return;
        }
        this.carregarPdmsDisponiveis();
      },
    },
    "form.idPdm": {
      handler(novoValor) {
        if (this.carregamentoInicial) return;
        const idNormalizado = this.normalizarIdentificador(novoValor);
        if (idNormalizado === null || idNormalizado === undefined) {
          this.eixosPdmDisponiveis = [];
          this.form.idsEixosPdm = [];
          this.form.idPdm = null;
          this.form.nomePdm = "";
          return;
        }
        const selecionado = this.pdmsDisponiveis.find(
          (pdm) => this.identificarPdm(pdm) === idNormalizado
        );
        if (selecionado) {
          this.form.nomePdm =
            selecionado.nome ||
            selecionado.titulo ||
            selecionado.descricao ||
            "";
        }
        this.carregarEixosDoPdm(idNormalizado);
      },
    },
    datas: {
      deep: true,
      handler() {
        this.registrarAlteracao();
      },
    },
    etapaAtual() {
      if (!this.carregamentoInicial && !this.emEdicao) {
        this.salvarRascunho();
      }
      if (this.etapaAtual === "basico") {
        this.$nextTick(() => this.tentarSincronizarMunicipio());
      }
    },
  },
  mounted() {
    window.addEventListener("beforeunload", this.prevenirSaida);
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.prevenirSaida);
    clearTimeout(this.rascunhoTimeout);
    clearTimeout(this.autosaveCampoTimeout);
  },
  beforeRouteLeave(to, from, next) {
    if (this.deveAlertarSaida) {
      const confirmar = window.confirm(
        "Deseja sair sem salvar? As informações preenchidas serão perdidas."
      );
      if (!confirmar) {
        return next(false);
      }
    }
    next();
  },
  methods: {
    estadoInicial() {
      return {
        titulo: "",
        descricao: "",
        idPdm: null,
        nomePdm: "",
        idsEixosPdm: [],
        idMunicipio: undefined,
        dataInicio: null,
        dataFim: null,
        situacao: "Planejamento",
        prioridade: "Media",
        nivelSaude: "Saudavel",
        orcamentoPlanejado: null,
        orcamentoExecutado: null,
        objetivos: "",
        escopo: "",
        acoes: [],
        equipes: [],
        orcamentos: [],
        metricas: [],
      };
    },
    definirAlerta(tipo, mensagem) {
      this.alerta = { tipo, mensagem };
    },
    limparErroData(limparMensagem = false) {
      this.erros.dataInicio = "";
      this.erros.dataFim = "";
      if (this.erros.periodoPrincipal) {
        this.erros.periodoPrincipal = false;
      }
      if (limparMensagem && this.alerta.tipo === "erro") {
        this.definirAlerta("", "");
      }
    },
    manipularEntradaData(evento, campo, objeto = this.datas) {
      if (!evento || !evento.target) return;

      // Para campos de input type="date", o valor já vem no formato ISO (AAAA-MM-DD)
      const valor = evento.target.value;

      if (Array.isArray(objeto)) {
        return;
      }

      if (objeto && typeof objeto === "object") {
        objeto[campo] = valor;

        // Se estamos alterando this.datas, precisamos sincronizar com this.form
        if (objeto === this.datas) {
          this.form[campo] = valor || null;
          this.validarPeriodoPrincipal();
          this.registrarAlteracao();
        }
      }
    },
    alternarDetalhesAcao(acao) {
      if (!acao) return;
      const estadoAtual = Boolean(acao.__colapsada);
      this.$set(acao, "__colapsada", !estadoAtual);
    },
    alternarDetalhesEntrega(entrega) {
      if (!entrega) return;
      const estadoAtual = Boolean(entrega.__colapsada);
      this.$set(entrega, "__colapsada", !estadoAtual);
    },
    formatarPercentual(valor) {
      const numero = Number(valor);
      if (Number.isNaN(numero)) {
        return "—";
      }
      if (numero % 1 === 0) {
        return `${numero}%`;
      }
      return `${numero.toFixed(1)}%`;
    },
    resumoPeriodoAcao(acao) {
      if (!acao) return "Defina datas para acompanhar a ação";
      const inicio = this.formatarDataBr(acao.dataInicio);
      const fim = this.formatarDataBr(acao.dataFim);
      const limite = this.formatarDataBr(acao.dataLimite);
      if (inicio || fim) {
        return `${inicio || "Início indefinido"} até ${
          fim || "fim indefinido"
        }`;
      }
      if (limite) {
        return `Limite em ${limite}`;
      }
      return "Sem período definido";
    },
    resumoPrazoEntrega(entrega) {
      if (!entrega) return "Sem prazo definido";
      const limite = this.formatarDataBr(entrega.dataLimite);
      return limite ? `Prazo: ${limite}` : "Sem prazo definido";
    },
    resumoPrazoTarefa(tarefa) {
      if (!tarefa) return "Sem prazo definido";
      const limite = this.formatarDataBr(tarefa.dataLimite);
      return limite ? `Prazo: ${limite}` : "Sem prazo definido";
    },
    formatarDataBr(valor) {
      if (!valor) return "";
      if (typeof valor === "string") {
        const texto = valor.trim();
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(texto)) {
          return texto;
        }
        if (/^\d{4}-\d{2}-\d{2}/.test(texto)) {
          const [ano, mes, dia] = texto.slice(0, 10).split("-");
          return `${dia}/${mes}/${ano}`;
        }
      }
      const data = new Date(valor);
      if (Number.isNaN(data.getTime())) return "";
      const dia = String(data.getDate()).padStart(2, "0");
      const mes = String(data.getMonth() + 1).padStart(2, "0");
      const ano = String(data.getFullYear());
      return `${dia}/${mes}/${ano}`;
    },
    converterDataParaIso(valor) {
      if (!valor) return null;
      const texto = String(valor).trim();
      if (!texto) return null;
      if (/^\d{4}-\d{2}-\d{2}/.test(texto)) {
        const base = texto.slice(0, 10);
        const [ano, mes, dia] = base.split("-").map((parte) => Number(parte));
        if (this.dataValida(dia, mes, ano)) {
          return `${String(ano).padStart(4, "0")}-${String(mes).padStart(
            2,
            "0"
          )}-${String(dia).padStart(2, "0")}`;
        }
        return null;
      }
      const apenasDigitos = texto.replace(/\D/g, "");
      if (apenasDigitos.length !== 8) return null;
      const dia = Number(apenasDigitos.slice(0, 2));
      const mes = Number(apenasDigitos.slice(2, 4));
      const ano = Number(apenasDigitos.slice(4));
      if (!this.dataValida(dia, mes, ano)) return null;
      return `${String(ano).padStart(4, "0")}-${String(mes).padStart(
        2,
        "0"
      )}-${String(dia).padStart(2, "0")}`;
    },
    dataValida(dia, mes, ano) {
      if (!dia || !mes || !ano) return false;
      if (mes < 1 || mes > 12) return false;
      if (dia < 1 || dia > 31) return false;
      const data = new Date(ano, mes - 1, dia);
      return (
        data.getFullYear() === ano &&
        data.getMonth() === mes - 1 &&
        data.getDate() === dia
      );
    },
    isValidDateISO(valor) {
      if (!valor || typeof valor !== "string") return false;
      const regex = /^\d{4}-\d{2}-\d{2}$/;
      if (!regex.test(valor)) return false;
      const data = new Date(valor);
      return (
        !isNaN(data.getTime()) && valor === data.toISOString().slice(0, 10)
      );
    },
    atualizarDataAcao(acao, campo, valorIso) {
      if (!valorIso) {
        acao[campo] = "";
        return;
      }
      // Converte de ISO (YYYY-MM-DD) para formato brasileiro (DD/MM/AAAA)
      const [ano, mes, dia] = valorIso.split("-");
      acao[campo] = `${dia}/${mes}/${ano}`;
    },
    validarPeriodoPrincipal() {
      // Se não tem ambas as datas, não há erro de período
      if (!this.form.dataInicio || !this.form.dataFim) {
        this.erros.periodoPrincipal = false;
        return true;
      }

      const inicio = new Date(this.form.dataInicio);
      const fim = new Date(this.form.dataFim);

      // Verifica se as datas são válidas
      if (Number.isNaN(inicio.getTime())) {
        this.erros.dataInicio = "Data de início inválida.";
        return false;
      }

      if (Number.isNaN(fim.getTime())) {
        this.erros.dataFim = "Data de fim inválida.";
        return false;
      }

      // Verifica se a data de fim é posterior ou igual à data de início
      if (inicio > fim) {
        this.erros.dataFim =
          "A data de fim deve ser posterior ou igual à data de início.";
        this.erros.periodoPrincipal = true;
        this.definirAlerta(
          "erro",
          "A data de fim deve ser posterior ou igual à data de início."
        );
        return false;
      }

      // Verifica se as datas não são muito antigas ou futuras
      const hoje = new Date();
      const anoAtual = hoje.getFullYear();
      const anoMinimo = 1900;
      const anoMaximo = anoAtual + 10;

      if (
        inicio.getFullYear() < anoMinimo ||
        inicio.getFullYear() > anoMaximo
      ) {
        this.erros.dataInicio = `A data de início deve estar entre ${anoMinimo} e ${anoMaximo}.`;
        return false;
      }

      if (fim.getFullYear() < anoMinimo || fim.getFullYear() > anoMaximo) {
        this.erros.dataFim = `A data de fim deve estar entre ${anoMinimo} e ${anoMaximo}.`;
        return false;
      }

      // Todas as validações passaram
      this.erros.dataInicio = "";
      this.erros.dataFim = "";
      this.erros.periodoPrincipal = false;
      this.limparErroData(true);
      return true;
    },
    validarTitulo() {
      if (!this.form.titulo || this.form.titulo.trim().length < 3) {
        this.erros.titulo = "O título deve ter pelo menos 3 caracteres.";
      } else {
        this.erros.titulo = "";
      }
    },
    identificadorMetrica(metrica) {
      if (!metrica) return null;
      return (
        metrica.idMetrica ?? metrica.id ?? metrica.__id ?? metrica.nome ?? null
      );
    },
    obterIdMetricaPersistida(metrica) {
      const id = this.normalizarIdentificador(
        metrica?.idMetrica ?? metrica?.id ?? null
      );
      return typeof id === "number" && id > 0 ? id : null;
    },
    obterIdAcaoPersistida(acao) {
      const id = this.normalizarIdentificador(
        acao?.idMarco ?? acao?.idAcao ?? null
      );
      return typeof id === "number" && id > 0 ? id : null;
    },
    podeSalvarVinculosAutomaticamente(acao) {
      return Boolean(
        this.idProjetoAtivo && this.obterIdAcaoPersistida(acao)
      );
    },
    podeSalvarVinculoMetrica(acao, metrica) {
      return Boolean(
        this.podeSalvarVinculosAutomaticamente(acao) &&
          this.obterIdMetricaPersistida(metrica)
      );
    },
    definirMetricasVinculadasAcao(acao, lista, silencioso = false) {
      if (!acao) return;
      const valores = Array.isArray(lista) ? lista : [];
      if (silencioso) {
        this.alteracaoSilenciosaVinculoMetrica = true;
      }
      this.$set(acao, "metricasVinculadas", valores);
      if (silencioso) {
        this.$nextTick(() => {
          this.alteracaoSilenciosaVinculoMetrica = false;
        });
      }
    },
    normalizarListaMetricasVinculadas(lista) {
      return (Array.isArray(lista) ? lista : [])
        .map((item) => this.normalizarIdentificador(item))
        .filter((item) => item !== null && item !== undefined);
    },
    prepararAlteracaoMetricasAcao(acao) {
      if (!acao) return;
      if (!Array.isArray(acao.__metricasVinculadasConfirmadas)) {
        this.$set(
          acao,
          "__metricasVinculadasConfirmadas",
          Array.isArray(acao.metricasVinculadas)
            ? [...acao.metricasVinculadas]
            : []
        );
      }
      this.$set(acao, "__alteradoAntesMetricas", this.alterado);
    },
    async salvarMetricasVinculadasAcao(acao) {
      if (!acao) return;

      if (!this.podeSalvarVinculosAutomaticamente(acao)) {
        this.registrarAlteracao();
        return;
      }

      const alteradoAntes = Boolean(acao.__alteradoAntesMetricas);
      const listaAnterior = Array.isArray(acao.__metricasVinculadasConfirmadas)
        ? [...acao.__metricasVinculadasConfirmadas]
        : [];
      const listaAtual = Array.isArray(acao.metricasVinculadas)
        ? [...acao.metricasVinculadas]
        : [];
      const anteriores = this.normalizarListaMetricasVinculadas(listaAnterior);
      const atuais = this.normalizarListaMetricasVinculadas(listaAtual);
      const adicionadas = atuais.filter((id) => !anteriores.includes(id));
      const removidas = anteriores.filter((id) => !atuais.includes(id));

      if (!adicionadas.length && !removidas.length) {
        this.alterado = alteradoAntes;
        return;
      }

      const mapaMetricas = new Map(
        this.form.metricas.map((metrica) => [
          this.normalizarIdentificador(this.identificadorMetrica(metrica)),
          metrica,
        ])
      );
      const chave = `acao:${this.idProjetoAtivo}:${this.obterIdAcaoPersistida(
        acao
      )}`;
      this.$set(this.metricasVinculosSalvando, chave, true);
      this.salvandoEtapa = true;

      try {
        let resposta = null;
        const operacoes = [
          ...adicionadas.map((id) => ({ id, vinculado: true })),
          ...removidas.map((id) => ({ id, vinculado: false })),
        ];

        for (const operacao of operacoes) {
          const metrica = mapaMetricas.get(operacao.id);
          if (!this.podeSalvarVinculoMetrica(acao, metrica)) {
            continue;
          }

          resposta = await definirVinculoMetricaAcao(
            this.idProjetoAtivo,
            this.obterIdAcaoPersistida(acao),
            this.obterIdMetricaPersistida(metrica),
            operacao.vinculado
          );
        }

        const metricasIds = resposta?.data?.metricasIds;
        const listaConfirmada = Array.isArray(metricasIds)
          ? metricasIds
          : listaAtual;

        this.definirMetricasVinculadasAcao(acao, listaConfirmada, true);
        this.$set(
          acao,
          "__metricasVinculadasConfirmadas",
          [...listaConfirmada]
        );
        this.alterado = alteradoAntes;
      } catch (error) {
        this.definirMetricasVinculadasAcao(acao, listaAnterior, true);
        this.$set(
          acao,
          "__metricasVinculadasConfirmadas",
          [...listaAnterior]
        );
        this.alterado = alteradoAntes;
        const mensagem =
          error?.response?.data?.mensagem ||
          "Não foi possível salvar o vínculo da métrica.";
        if (this.$toasted) {
          this.$toasted.error(mensagem);
        } else {
          this.definirAlerta("erro", mensagem);
        }
      } finally {
        this.$delete(this.metricasVinculosSalvando, chave);
        this.salvandoEtapa = false;
      }
    },
    normalizarIdentificador(valor) {
      if (valor === null || valor === undefined || valor === "") return null;
      if (typeof valor === "number") return valor;
      const numero = Number(valor);
      if (!Number.isNaN(numero) && String(numero) === String(valor).trim()) {
        return numero;
      }
      return typeof valor === "string" ? valor.trim() : valor;
    },
    identificarPdm(pdm) {
      if (!pdm || typeof pdm !== "object") return null;
      const id =
        pdm.idPdm ?? pdm.idPlanoMunicipal ?? pdm.pdmId ?? pdm.idPlano ?? pdm.id;
      return this.normalizarIdentificador(id);
    },
    identificarEixo(eixo) {
      if (!eixo || typeof eixo !== "object") return null;
      const id =
        eixo.idEixo ??
        eixo.idEixoEstrategico ??
        eixo.idEixoPdm ??
        eixo.idPdmEixo ??
        eixo.id ??
        eixo.idMarco;
      return this.normalizarIdentificador(id);
    },
    extrairIdsEixos(dados) {
      if (!dados || typeof dados !== "object") return [];
      const colecoes = [];
      const possiveisListas = [
        dados.idsEixosPdm,
        dados.idsEixos,
        dados.idsEixosEstrategicos,
        dados.eixosIds,
        dados.eixos?.map?.((item) => this.identificarEixo(item)),
      ];
      possiveisListas.forEach((lista) => {
        if (Array.isArray(lista)) {
          colecoes.push(...lista);
        }
      });
      if (dados.idEixoEstrategico) {
        colecoes.push(dados.idEixoEstrategico);
      }
      const idsNormalizados = colecoes
        .map((item) => this.normalizarIdentificador(item))
        .filter((item) => item !== null && item !== undefined);
      return [...new Set(idsNormalizados)];
    },
    registrarAlteracao() {
      if (this.carregamentoInicial) return;
      if (this.alteracaoSilenciosaVinculoMetrica) return;
      this.alterado = true;
      if (this.emEdicao) return;
      clearTimeout(this.rascunhoTimeout);
      this.rascunhoTimeout = setTimeout(() => {
        this.salvarRascunho();
      }, 400);
    },
    registrarAlteracaoNaoAutosalva() {
      if (this.carregamentoInicial) return;
      this.alteracoesNaoAutosalvas = true;
      this.registrarAlteracao();
    },
    deveAutosalvarCampoProjeto(evento) {
      if (this.carregamentoInicial || this.salvando || this.salvandoEtapa) {
        return false;
      }

      if (!this.idProjetoAtivo || !this.emEdicao) {
        return false;
      }

      const alvo = evento?.target;
      if (!alvo || !alvo.matches) {
        return false;
      }

      if (alvo.disabled || alvo.readOnly) {
        return false;
      }

      return (
        alvo.matches("input, select, textarea") &&
        Boolean(alvo.dataset?.autosaveCampo)
      );
    },
    agendarAutosaveCampoProjeto(evento) {
      const alvo = evento?.target;
      if (
        alvo?.matches?.("input, select, textarea") &&
        !alvo.dataset?.autosaveCampo &&
        this.emEdicao
      ) {
        this.alteracoesNaoAutosalvas = true;
      }

      if (!this.deveAutosalvarCampoProjeto(evento)) {
        return;
      }

      const campo = alvo.dataset.autosaveCampo;
      this.$nextTick(() => this.agendarAutosaveCampoPorNome(campo));
    },
    agendarAutosaveCampoPorNome(campo) {
      if (
        !campo ||
        this.carregamentoInicial ||
        !this.emEdicao ||
        !this.idProjetoAtivo
      ) {
        return;
      }

      this.$set(this.camposAutosavePendentes, campo, true);
      this.autosaveCampoPendente = true;
      clearTimeout(this.autosaveCampoTimeout);
      this.autosaveCampoTimeout = setTimeout(() => {
        this.executarAutosaveCampoProjeto();
      }, 700);
    },
    async executarAutosaveCampoProjeto() {
      if (this.autosaveCampoExecutando) {
        this.autosaveCampoPendente = true;
        return;
      }

      const camposPendentes = Object.keys(this.camposAutosavePendentes);
      if (
        this.salvando ||
        this.salvandoEtapa ||
        !camposPendentes.length ||
        !this.idProjetoAtivo ||
        !this.emEdicao
      ) {
        return;
      }

      const campo = camposPendentes[0];
      this.autosaveCampoExecutando = true;
      let salvou = false;
      try {
        salvou = await this.autosalvarProjetoEmSegundoPlano(campo);
        if (salvou) {
          this.$delete(this.camposAutosavePendentes, campo);
        }
      } finally {
        this.autosaveCampoExecutando = false;
        this.autosaveCampoPendente =
          Object.keys(this.camposAutosavePendentes).length > 0;
        if (salvou && this.autosaveCampoPendente) {
          clearTimeout(this.autosaveCampoTimeout);
          this.autosaveCampoTimeout = setTimeout(() => {
            this.executarAutosaveCampoProjeto();
          }, 300);
        } else if (salvou && !this.alteracoesNaoAutosalvas) {
          this.alterado = false;
        }
      }
    },
    valorCampoProjetoParaAutosave(campo) {
      if (campo === "dataInicio" || campo === "dataFim") {
        return this.form[campo] || null;
      }
      if (campo === "idPdm") {
        return this.normalizarIdentificador(this.form.idPdm);
      }
      if (campo === "idsEixosPdm") {
        return (this.form.idsEixosPdm || [])
          .map((id) => this.normalizarIdentificador(id))
          .filter((id) => id !== null && id !== undefined);
      }
      if (campo === "idMunicipio") {
        return this.form.idMunicipio || null;
      }
      return this.form[campo];
    },
    async autosalvarProjetoEmSegundoPlano(campo) {
      if (campo === "titulo" && !this.form.titulo) {
        return true;
      }

      if (
        (campo === "dataInicio" || campo === "dataFim") &&
        !this.validarPeriodoPrincipal()
      ) {
        return true;
      }

      try {
        const payload = {
          campo,
          valor: this.valorCampoProjetoParaAutosave(campo),
          versao: this.versaoProjeto,
          origem: "autosave-campo-assistente-projeto",
        };
        const resposta = await atualizarCampoProjeto(
          this.idProjetoAtivo,
          payload
        );
        const versao = this.extrairVersaoProjeto(resposta?.data);
        if (versao !== null) {
          this.versaoProjeto = versao;
        }
        return true;
      } catch (error) {
        if (await this.tratarConflitoProjeto(error)) {
          return true;
        }
        // eslint-disable-next-line no-console
        console.error("[gestao-projetos] autosave-projeto", error);
        return false;
      }
    },
    async tratarConflitoProjeto(error) {
      if (Number(error?.response?.status) !== 409) {
        return false;
      }

      clearTimeout(this.autosaveCampoTimeout);
      this.camposAutosavePendentes = {};
      this.autosaveCampoPendente = false;
      this.alteracoesNaoAutosalvas = false;
      await this.carregarProjeto(this.idProjetoAtivo);
      const mensagem =
        "Este projeto foi alterado em outra aba ou sessão. Os dados mais recentes foram recarregados; revise antes de continuar.";
      this.definirAlerta("erro", mensagem);
      this.$toasted && this.$toasted.error(mensagem);
      return true;
    },
    classeObrigatorio(valor) {
      return !valor ? "obrigatorio" : "";
    },
    aplicarMascaraTelefone(valor) {
      const numeros = String(valor || "")
        .replace(/\D/g, "")
        .slice(0, 11);
      if (!numeros) return "";
      if (numeros.length <= 2) {
        return `(${numeros}`;
      }
      if (numeros.length <= 6) {
        return `(${numeros.slice(0, 2)}) ${numeros.slice(2)}`;
      }
      if (numeros.length <= 10) {
        return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 6)}-${numeros.slice(
          6
        )}`;
      }
      return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 7)}-${numeros.slice(
        7,
        11
      )}`;
    },
    atualizarTelefone(evento, membro) {
      if (!evento || !evento.target || !membro) return;
      const mascarado = this.aplicarMascaraTelefone(evento.target.value);
      membro.telefoneUsuario = mascarado;
      evento.target.value = mascarado;
      this.registrarAlteracao();
    },
    aplicarMascaraData(valor) {
      const numeros = String(valor || "")
        .replace(/\D/g, "")
        .slice(0, 8);

      if (!numeros) return "";

      let resultado = "";

      // Dia
      if (numeros.length >= 1) {
        let dia = numeros.slice(0, 2);
        // Limita o dia a 31
        if (parseInt(dia) > 31) {
          dia = "31";
        }
        resultado = dia;
      }

      // Mês
      if (numeros.length >= 3) {
        let mes = numeros.slice(2, 4);
        // Limita o mês a 12
        if (parseInt(mes) > 12) {
          mes = "12";
        }
        resultado += `/${mes}`;
      }

      // Ano
      if (numeros.length >= 5) {
        const ano = numeros.slice(4, 8);
        resultado += `/${ano}`;
      }

      return resultado;
    },
    validarDataCompleta(valor) {
      if (!valor) return false;
      const regex = /^\d{2}\/\d{2}\/\d{4}$/;
      if (!regex.test(valor)) return false;

      const [dia, mes, ano] = valor.split("/").map(Number);

      // Validações básicas de intervalo
      if (dia < 1 || dia > 31) return false;
      if (mes < 1 || mes > 12) return false;
      if (ano < 1900 || ano > 2036) return false;

      return this.dataValida(dia, mes, ano);
    },
    validarDataExistente(valor) {
      if (!valor) return false;
      const regex = /^\d{2}\/\d{2}\/\d{4}$/;
      if (!regex.test(valor)) return false;

      const [dia, mes, ano] = valor.split("/").map(Number);

      // Validações básicas de intervalo
      if (dia < 1 || dia > 31) return false;
      if (mes < 1 || mes > 12) return false;
      if (ano < 1900 || ano > 2036) return false;

      // Verifica se é uma data válida usando o objeto Date
      const data = new Date(ano, mes - 1, dia);

      // Verifica se a data criada corresponde aos valores inseridos
      // (isso detecta casos como 31/02/2023 que seria inválido)
      return (
        data.getFullYear() === ano &&
        data.getMonth() === mes - 1 &&
        data.getDate() === dia &&
        !Number.isNaN(data.getTime())
      );
    },
    manipularEntradaDataComMascara(evento, campo) {
      if (!evento || !evento.target) return;

      const valorComMascara = this.aplicarMascaraData(evento.target.value);
      evento.target.value = valorComMascara;
      this.datas[campo] = valorComMascara;

      // Se a data estiver completa (DD/MM/AAAA), valida
      if (valorComMascara.length === 10) {
        if (!this.validarDataCompleta(valorComMascara)) {
          this.erros[campo] = "Formato de data inválido";
          this.form[campo] = null;
        } else if (!this.validarDataExistente(valorComMascara)) {
          this.erros[campo] = "Data inexistente";
          this.form[campo] = null;
        } else {
          // Data válida - limpa erro e converte para ISO
          this.erros[campo] = "";
          const valorIso = this.converterDataParaIso(valorComMascara);
          this.form[campo] = valorIso;
        }
      } else {
        // Data incompleta - limpa erro e campo
        this.erros[campo] = "";
        this.form[campo] = null;
      }

      this.validarPeriodoPrincipal();
      this.registrarAlteracao();
      this.limparErroData(true);
    },
    aoSelecionarDataCalendario(evento, campo) {
      if (!evento || !evento.target) return;

      const valorIso = evento.target.value;
      if (valorIso) {
        // Converte ISO para formato brasileiro e atualiza os dois campos
        const valorBr = this.formatarDataBr(valorIso);
        this.datas[campo] = valorBr;
        this.form[campo] = valorIso;

        // Limpa erros específicos do campo
        this.erros[campo] = "";

        this.validarPeriodoPrincipal();
        this.registrarAlteracao();
        this.limparErroData(true);
      }
    },
    aoAlterarMunicipio(municipio) {
      const idAnterior = this.form.idMunicipio || null;
      if (Array.isArray(municipio)) {
        const selecionado = municipio.find(
          (item) => item && (item.municipioId || item.id)
        );
        this.form.idMunicipio = selecionado
          ? Number(selecionado.municipioId || selecionado.id)
          : undefined;
        this.municipioFiltro.municipio = selecionado || null;
        this.municipioSelecionadoNome = selecionado?.nome || "";
      } else if (municipio && typeof municipio === "object") {
        this.form.idMunicipio =
          municipio.municipioId || municipio.id || undefined;
        this.municipioFiltro.municipio = municipio;
        this.municipioSelecionadoNome = municipio.nome || "";
      } else {
        this.form.idMunicipio = undefined;
        this.municipioFiltro.municipio = null;
        this.municipioSelecionadoNome = "";
      }
      this.registrarAlteracao();
      if ((this.form.idMunicipio || null) !== idAnterior) {
        this.agendarAutosaveCampoPorNome("idMunicipio");
      }
    },
    tentarSincronizarMunicipio(tentativa = 0) {
      if (tentativa > 10) return;
      if (!this.form.idMunicipio) {
        this.municipioFiltro.municipio = null;
        if (!this.municipioSelecionadoNome) {
          this.municipioSelecionadoNome = "";
        }
        return;
      }
      const combobox = this.$refs.municipioCombobox;
      const lista = combobox?.municipios;
      if (Array.isArray(lista) && lista.length) {
        const encontrado = lista.find(
          (item) => item.municipioId === this.form.idMunicipio
        );
        if (encontrado) {
          this.municipioFiltro.municipio = encontrado;
          this.municipioSelecionadoNome = encontrado.nome || "";
          return;
        }
      }
      setTimeout(() => this.tentarSincronizarMunicipio(tentativa + 1), 200);
    },
    async carregarOpcoes() {
      try {
        const { data } = await buscarOpcoes(this.uf);
        this.opcoes = {
          situacoes: data.situacoes || [],
          prioridades: data.prioridades || [],
          niveisSaude: data.niveisSaude || [],
        };
      } catch (error) {
        this.$toasted &&
          this.$toasted.error(
            "Não foi possível carregar as opções do assistente."
          );
        // eslint-disable-next-line no-console
        console.error("[gestao-projetos] opcoes-assistente", error);
      }
    },
    async carregarPdmsDisponiveis() {
      if (!this.form.idMunicipio) {
        this.pdmsDisponiveis = [];
        return;
      }
      this.carregandoPdms = true;
      try {
        const { data } = await buscarPdms({
          idMunicipio: this.form.idMunicipio,
        });
        const lista = Array.isArray(data) ? data : [];
        this.pdmsDisponiveis = lista;
        const selecionadoAtual = this.normalizarIdentificador(this.form.idPdm);
        if (selecionadoAtual !== null && selecionadoAtual !== undefined) {
          const encontrado = lista.find(
            (pdm) => this.identificarPdm(pdm) === selecionadoAtual
          );
          const existe = Boolean(encontrado);
          if (!existe) {
            this.form.idPdm = null;
            this.form.nomePdm = "";
          } else {
            this.form.idPdm = selecionadoAtual;
            this.form.nomePdm =
              encontrado.nome ||
              encontrado.titulo ||
              encontrado.descricao ||
              "";
          }
        }
      } catch (error) {
        this.pdmsDisponiveis = [];
        this.$toasted &&
          this.$toasted.error(
            "Não foi possível carregar os planos municipais disponíveis."
          );
        // eslint-disable-next-line no-console
        console.error("[gestao-projetos] carregar-pdms", error);
      } finally {
        this.carregandoPdms = false;
      }
    },
    async carregarEixosDoPdm(idPdm) {
      const idNormalizado = this.normalizarIdentificador(idPdm);
      if (idNormalizado === null || idNormalizado === undefined) {
        this.eixosPdmDisponiveis = [];
        this.form.idsEixosPdm = [];
        this.form.idPdm = null;
        this.form.nomePdm = "";
        return;
      }
      this.carregandoEixosPdm = true;
      try {
        const { data } = await buscarPdmDetalhe(idNormalizado);
        const eixos = Array.isArray(data?.eixos) ? data.eixos : [];
        this.eixosPdmDisponiveis = eixos;
        if (data) {
          this.form.nomePdm =
            data.nome || data.titulo || data.descricao || this.form.nomePdm;
        }
        const idsValidos = new Set(
          eixos
            .map((eixo) => this.identificarEixo(eixo))
            .filter((id) => id !== null)
        );
        const atuais = Array.isArray(this.form.idsEixosPdm)
          ? this.form.idsEixosPdm
          : [];
        this.form.idsEixosPdm = atuais
          .map((id) => this.normalizarIdentificador(id))
          .filter((id) => id !== null && idsValidos.has(id));
        this.form.idPdm = idNormalizado;
      } catch (error) {
        this.eixosPdmDisponiveis = [];
        this.form.idsEixosPdm = [];
        this.form.idPdm = null;
        this.$toasted &&
          this.$toasted.error(
            "Não foi possível carregar as áreas chave do PDM selecionado."
          );
        // eslint-disable-next-line no-console
        console.error("[gestao-projetos] carregar-eixos-pdm", error);
      } finally {
        this.carregandoEixosPdm = false;
      }
    },
    async carregarProjeto(idProjeto) {
      try {
        const { data } = await buscarProjeto(idProjeto);
        await this.popularFormularioComDetalhe(data);
        this.alterado = false;
        this.alteracoesNaoAutosalvas = false;
      } catch (error) {
        this.$toasted &&
          this.$toasted.error(
            "Não foi possível carregar o projeto para edição."
          );
        // eslint-disable-next-line no-console
        console.error("[gestao-projetos] carregar-projeto", error);
      }
    },
    async popularFormularioComDetalhe(dados, opcoes = {}) {
      if (!dados || typeof dados !== "object") return;

      const manterEtapa = Boolean(opcoes.manterEtapa);
      const etapaAnterior = this.etapaAtual;

      this.valoresMoedaEmEdicao = {};

      const metricasOrigem = (dados.metricas || []).map((item) => ({
        ...item,
        __id: item.__id || uid(),
      }));
      const mapaMetricas = new Map(
        metricasOrigem.map((metrica) => [
          this.identificadorMetrica(metrica),
          metrica,
        ])
      );
      const entregasOrigem = dados.entregas || [];
      const acoesOrigem = dados.acoes || dados.marcos || [];

      const acoes = (acoesOrigem || []).map((acao) => {
        const entregasRelacionadas = Array.isArray(acao.entregas)
          ? acao.entregas
          : entregasOrigem.filter((entrega) => {
              const idAcao = acao.idAcao || acao.idMarco;
              return entrega.idAcao === idAcao || entrega.idMarco === idAcao;
            });

        const metricasRelacionadas = this.normalizarMetricasDaAcao(
          acao.metricas || acao.metricasVinculadas || acao.metricasIds || [],
          mapaMetricas
        );

        metricasRelacionadas.forEach((metrica) => {
          const chave = this.identificadorMetrica(metrica);
          if (chave && !mapaMetricas.has(chave)) {
            mapaMetricas.set(chave, {
              ...metrica,
              __id: metrica.__id || uid(),
            });
          }
        });
        const metricasVinculadas = metricasRelacionadas
          .map((metrica) => this.identificadorMetrica(metrica))
          .filter(Boolean);

        return {
          ...acao,
          idAcao: acao.idAcao || acao.idMarco,
          __id: uid(),
          dataInicio: this.formatarDataBr(acao.dataInicio),
          dataFim: this.formatarDataBr(acao.dataFim),
          dataLimite: this.formatarDataBr(acao.dataLimite),
          __progressoManual: Boolean(acao.__progressoManual),
          __colapsada: acao.__colapsada === true,
          metricasVinculadas,
          __metricasVinculadasConfirmadas: [...metricasVinculadas],
          entregas: (entregasRelacionadas || []).map((entrega) => ({
            ...entrega,
            __id: uid(),
            responsavelNome:
              entrega.responsavelNome ||
              entrega.nomeResponsavel ||
              entrega.responsavel ||
              entrega.nomeUsuario ||
              "",
            responsavelEmail:
              entrega.emailResponsavel ||
              entrega.responsavelEmail ||
              entrega.usuario?.email ||
              "",
            dataLimite: this.formatarDataBr(entrega.dataLimite),
            dataConclusao: this.formatarDataBr(entrega.dataConclusao),
            percentualConclusao: this.normalizarPercentualEntrada(
              entrega.percentualConclusao
            ),
            peso: this.normalizarPercentualEntrada(entrega.peso),
            __progressoManual: Boolean(entrega.__progressoManual),
            __colapsada: entrega.__colapsada === true,
            tarefas: (entrega.tarefas || []).map((tarefa) => ({
              ...tarefa,
              __id: uid(),
              responsavelNome:
                tarefa.responsavelNome ||
                tarefa.nomeResponsavel ||
                tarefa.responsavel ||
                tarefa.usuario?.nome ||
                "",
              responsavelEmail:
                tarefa.emailResponsavel ||
                tarefa.responsavelEmail ||
                tarefa.usuario?.email ||
                "",
              dataLimite: this.formatarDataBr(tarefa.dataLimite),
              percentualConclusao: this.normalizarPercentualEntrada(
                tarefa.percentualConclusao
              ),
              peso: this.normalizarPercentualEntrada(tarefa.peso),
            })),
          })),
        };
      });

      const idPdmSelecionado = this.identificarPdm(dados);
      const idsEixosSelecionados = this.extrairIdsEixos(dados);

      this.form = {
        ...this.estadoInicial(),
        ...dados,
        idPdm: idPdmSelecionado,
        nomePdm: dados.nomePdm || dados.nomePlanoMunicipal || "",
        idsEixosPdm: idsEixosSelecionados,
        dataInicio: this.converterDataParaIso(dados.dataInicio),
        dataFim: this.converterDataParaIso(dados.dataFim),
        idMunicipio: dados.idMunicipio || undefined,
        acoes,
        equipes: (dados.equipes || []).map((item) => ({
          ...item,
          __id: uid(),
          nomeUsuario:
            item.nomeUsuario || item.nome || item.usuario?.nome || "",
          emailUsuario: item.emailUsuario || item.usuario?.email || "",
          telefoneUsuario: this.aplicarMascaraTelefone(
            item.telefoneUsuario || item.usuario?.telefone || ""
          ),
          idInstituicao: item.idInstituicao || item.instituicaoId || undefined,
          instituicaoNome:
            item.nomeInstituicao ||
            item.instituicaoNome ||
            item.instituicao ||
            "",
        })),
        orcamentos: (dados.orcamentos || []).map((item) => ({
          ...item,
          __id: uid(),
        })),
        metricas: Array.from(mapaMetricas.values()),
      };
      this.versaoProjeto = this.extrairVersaoProjeto(dados);
      this.quantidadeAcoesPersistidas = acoes.filter(
        (acao) => acao.idAcao || acao.idMarco
      ).length;
      this.camposAutosavePendentes = {};
      this.autosaveCampoPendente = false;
      this.alteracoesNaoAutosalvas = false;

      this.datas = {
        dataInicio: this.formatarDataBr(dados.dataInicio),
        dataFim: this.formatarDataBr(dados.dataFim),
      };

      this.erros.periodoPrincipal = false;
      this.errosPesos = [];
      this.municipioSelecionadoNome = dados.nomeMunicipio || "";

      const idExtraido = this.extrairIdProjeto(dados);
      if (idExtraido) {
        this.projetoPersistidoId = idExtraido;
        this.emEdicao = true;
      }

      await this.carregarPdmsDisponiveis();
      if (this.form.idPdm) {
        await this.carregarEixosDoPdm(this.form.idPdm);
      }
      this.$nextTick(() => {
        this.tentarSincronizarMunicipio();
        this.prepararProgressosIniciais();
      });

      if (manterEtapa) {
        this.$nextTick(() => {
          this.etapaAtual = etapaAnterior;
        });
      }
    },
    normalizarMetricasDaAcao(lista, mapaMetricas = new Map()) {
      if (!Array.isArray(lista)) return [];
      return lista.map((item) => {
        if (item && typeof item === "object") {
          const chave = this.identificadorMetrica(item);
          return (
            mapaMetricas.get(chave) || { ...item, __id: item.__id || uid() }
          );
        }
        const referencia = item;
        const existente = mapaMetricas.get(referencia);
        if (existente) {
          return existente;
        }
        return {
          idMetrica: typeof referencia === "number" ? referencia : null,
          __id: typeof referencia === "string" ? referencia : uid(),
          nome: typeof referencia === "string" ? referencia : "",
        };
      });
    },
    adicionarAcao() {
      this.form.acoes.push({
        __id: uid(),
        titulo: "",
        descricao: "",
        dataInicio: "",
        dataFim: "",
        dataLimite: "",
        situacao: this.form.situacao,
        progresso: 0,
        __progressoManual: false,
        __colapsada: false,
        metricasVinculadas: [],
        __metricasVinculadasConfirmadas: [],
        entregas: [],
      });
      this.registrarAlteracaoNaoAutosalva();
      this.$nextTick(() => {
        const acao = this.form.acoes[this.form.acoes.length - 1];
        if (acao) {
          this.prepararProgressosIniciais([acao]);
        }
      });
    },
    async removerAcao(indice) {
      const acao = this.form.acoes[indice];
      if (!acao) return;

      const titulo = String(acao.titulo || "esta ação").trim();
      const confirmou = window.confirm(
        `Confirma a exclusão de ${titulo}? A ação será enviada para a lixeira e poderá ser restaurada.`
      );
      if (!confirmou) return;

      const idAcao = acao.idAcao || acao.idMarco;
      if (!idAcao || !this.emEdicao || !this.idProjetoAtivo) {
        this.form.acoes.splice(indice, 1);
        this.registrarAlteracaoNaoAutosalva();
        return;
      }

      try {
        const resposta = await excluirAcaoProjeto(
          this.idProjetoAtivo,
          idAcao,
          {
            confirmar: true,
            versao: this.versaoProjeto,
            origem: "exclusao-explicita-assistente-projeto",
          }
        );
        const versao = this.extrairVersaoProjeto(resposta?.data);
        if (versao !== null) {
          this.versaoProjeto = versao;
        } else if (Number.isFinite(Number(this.versaoProjeto))) {
          this.versaoProjeto = Number(this.versaoProjeto) + 1;
        }
        this.form.acoes.splice(indice, 1);
        this.quantidadeAcoesPersistidas = Math.max(
          0,
          this.quantidadeAcoesPersistidas - 1
        );
        this.$toasted &&
          this.$toasted.success(
            "Ação enviada para a lixeira. Ela poderá ser restaurada."
          );
        if (this.lixeiraAberta) {
          await this.carregarLixeiraAcoes();
        }
        this.$nextTick(() => {
          if (
            !this.alteracoesNaoAutosalvas &&
            !Object.keys(this.camposAutosavePendentes).length
          ) {
            this.alterado = false;
          }
        });
      } catch (error) {
        if (await this.tratarConflitoProjeto(error)) {
          return;
        }
        this.$toasted &&
          this.$toasted.error(
            "Não foi possível excluir a ação. Nenhuma alteração foi feita."
          );
        // eslint-disable-next-line no-console
        console.error("[gestao-projetos] excluir-acao", error);
      }
    },
    async alternarLixeiraAcoes() {
      this.lixeiraAberta = !this.lixeiraAberta;
      if (this.lixeiraAberta) {
        await this.carregarLixeiraAcoes();
      }
    },
    async carregarLixeiraAcoes() {
      if (!this.idProjetoAtivo) return;
      try {
        this.carregandoLixeira = true;
        const resposta = await listarAcoesExcluidasProjeto(this.idProjetoAtivo);
        this.acoesExcluidas = Array.isArray(resposta?.data) ? resposta.data : [];
      } catch (error) {
        this.$toasted &&
          this.$toasted.error("Não foi possível carregar a lixeira de ações.");
        // eslint-disable-next-line no-console
        console.error("[gestao-projetos] carregar-lixeira-acoes", error);
      } finally {
        this.carregandoLixeira = false;
      }
    },
    async restaurarAcaoExcluida(acao) {
      if (!acao?.idMarco || !this.idProjetoAtivo) return;
      const confirmou = window.confirm(
        `Confirma a restauração de ${acao.titulo || "esta ação"}?`
      );
      if (!confirmou) return;

      try {
        await restaurarAcaoProjeto(this.idProjetoAtivo, acao.idMarco, {
          confirmarRestauracao: true,
          versao: this.versaoProjeto,
          origem: "restauracao-lixeira-assistente-projeto",
        });
        await this.carregarProjeto(this.idProjetoAtivo);
        await this.carregarLixeiraAcoes();
        this.$toasted && this.$toasted.success("Ação restaurada com sucesso.");
      } catch (error) {
        if (await this.tratarConflitoProjeto(error)) {
          if (this.lixeiraAberta) await this.carregarLixeiraAcoes();
          return;
        }
        this.$toasted &&
          this.$toasted.error("Não foi possível restaurar a ação.");
        // eslint-disable-next-line no-console
        console.error("[gestao-projetos] restaurar-acao", error);
      }
    },
    adicionarEntrega(indiceAcao) {
      this.form.acoes[indiceAcao].entregas.push({
        __id: uid(),
        titulo: "",
        descricao: "",
        idUsuarioResponsavel: undefined,
        responsavelNome: "",
        responsavelEmail: "",
        dataLimite: "",
        situacao: this.form.situacao,
        percentualConclusao: 0,
        peso: 0,
        __progressoManual: false,
        __colapsada: false,
        tarefas: [],
      });
      this.registrarAlteracaoNaoAutosalva();
      this.$nextTick(() => {
        const acao = this.form.acoes[indiceAcao];
        if (!acao) return;
        const indiceEntrega = acao.entregas.length - 1;
        if (indiceEntrega >= 0) {
          this.aoAlterarEntrega(indiceAcao, indiceEntrega);
        }
      });
    },
    removerEntrega(indiceAcao, indiceEntrega) {
      this.form.acoes[indiceAcao].entregas.splice(indiceEntrega, 1);
      this.registrarAlteracaoNaoAutosalva();
      const acao = this.form.acoes[indiceAcao];
      if (acao) {
        this.prepararProgressosIniciais([acao]);
      }
    },
    adicionarTarefa(indiceAcao, indiceEntrega) {
      this.form.acoes[indiceAcao].entregas[indiceEntrega].tarefas.push({
        __id: uid(),
        titulo: "",
        descricao: "",
        idUsuarioResponsavel: undefined,
        responsavelNome: "",
        responsavelEmail: "",
        dataLimite: "",
        situacao: "Planejamento",
        percentualConclusao: 0,
        peso: 0,
      });
      this.registrarAlteracaoNaoAutosalva();
      this.$nextTick(() => this.aoAlterarTarefa(indiceAcao, indiceEntrega));
    },
    removerTarefa(indiceAcao, indiceEntrega, indiceTarefa) {
      this.form.acoes[indiceAcao].entregas[indiceEntrega].tarefas.splice(
        indiceTarefa,
        1
      );
      this.registrarAlteracaoNaoAutosalva();
      this.$nextTick(() => this.aoAlterarTarefa(indiceAcao, indiceEntrega));
    },
    adicionarMembro() {
      this.form.equipes.push({
        __id: uid(),
        idUsuario: undefined,
        nomeUsuario: "",
        emailUsuario: "",
        telefoneUsuario: "",
        idInstituicao: undefined,
        instituicaoNome: "",
        papel: "",
        responsabilidades: "",
        alocacaoPercentual: null,
      });
      this.registrarAlteracaoNaoAutosalva();
    },
    removerMembro(indice) {
      this.form.equipes.splice(indice, 1);
      this.registrarAlteracaoNaoAutosalva();
    },
    adicionarOrcamento() {
      this.form.orcamentos.push({
        __id: uid(),
        categoria: "",
        valorPlanejado: null,
        valorExecutado: null,
      });
      this.registrarAlteracaoNaoAutosalva();
    },
    removerOrcamento(indice) {
      this.form.orcamentos.splice(indice, 1);
      this.registrarAlteracaoNaoAutosalva();
    },
    adicionarMetrica() {
      this.form.metricas.push({
        __id: uid(),
        nome: "",
        tipo: "KPI",
        descricao: "",
        linhaBase: null,
        meta: null,
        valorAtual: null,
      });
      this.registrarAlteracaoNaoAutosalva();
    },
    removerMetrica(indice) {
      this.form.metricas.splice(indice, 1);
      this.registrarAlteracaoNaoAutosalva();
    },
    normalizarData(campo) {
      const valor = (this.datas[campo] || "").trim();

      if (!valor) {
        this.form[campo] = null;
        this.validarPeriodoPrincipal();
        this.registrarAlteracao();
        return;
      }

      // Verifica se a data está no formato completo DD/MM/AAAA
      if (this.validarDataCompleta(valor)) {
        const valorIso = this.converterDataParaIso(valor);
        this.form[campo] = valorIso;
        this.validarPeriodoPrincipal();
        this.registrarAlteracao();
      } else if (valor.length >= 8) {
        // Se tem pelo menos 8 caracteres mas não está no formato correto
        this.erros.periodoPrincipal = true;
        this.definirAlerta("erro", "Use o formato DD/MM/AAAA para as datas.");
      }
    },
    normalizarDataAcao(acao, campo) {
      if (!acao[campo]) {
        acao[campo] = "";
        return;
      }
      const iso = this.converterDataParaIso(acao[campo]);
      if (!iso) {
        this.$toasted && this.$toasted.error("Data inválida. Use DD/MM/AAAA.");
        acao[campo] = "";
        return;
      }
      acao[campo] = this.formatarDataBr(iso);
      this.registrarAlteracao();
      this.validarDatasAcao(acao);
    },
    validarDatasAcao(acao) {
      if (!acao || typeof acao !== "object") return true;

      const inicioIso = this.converterDataParaIso(acao.dataInicio);
      const fimIso = this.converterDataParaIso(acao.dataFim);
      const limiteIso = this.converterDataParaIso(acao.dataLimite);

      if (inicioIso && fimIso && new Date(inicioIso) > new Date(fimIso)) {
        this.$toasted &&
          this.$toasted.error(
            "A data final da ação deve ser posterior à data inicial."
          );
        acao.dataFim = "";
        return false;
      }

      if (fimIso && limiteIso && new Date(fimIso) > new Date(limiteIso)) {
        this.$toasted &&
          this.$toasted.error(
            "A data final da ação deve ser anterior ou igual à data limite."
          );
        acao.dataFim = "";
        return false;
      }

      if (inicioIso && limiteIso && new Date(inicioIso) > new Date(limiteIso)) {
        this.$toasted &&
          this.$toasted.error(
            "A data limite deve ser posterior à data inicial da ação."
          );
        acao.dataLimite = "";
        return false;
      }

      return true;
    },
    normalizarDataEntrega(entrega, campo) {
      if (!entrega[campo]) return;
      const iso = this.converterDataParaIso(entrega[campo]);
      if (!iso) {
        this.$toasted && this.$toasted.error("Data inválida. Use DD/MM/AAAA.");
        entrega[campo] = "";
        return;
      }
      entrega[campo] = this.formatarDataBr(iso);
      this.registrarAlteracao();
    },
    normalizarDataTarefa(tarefa, campo) {
      if (!tarefa[campo]) return;
      const iso = this.converterDataParaIso(tarefa[campo]);
      if (!iso) {
        this.$toasted && this.$toasted.error("Data inválida. Use DD/MM/AAAA.");
        tarefa[campo] = "";
        return;
      }
      tarefa[campo] = this.formatarDataBr(iso);
      this.registrarAlteracao();
    },
    rolarTopo() {
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    rolarAcoes() {
      this.$refs.acoes?.scrollIntoView({ behavior: "smooth", block: "center" });
    },
    async voltar() {
      if (this.indiceEtapa > 0) {
        const anterior = this.etapas[this.indiceEtapa - 1].id;
        await this.navegarParaEtapa(anterior);
      } else {
        // Voltar para a tela de projetos quando estiver na primeira etapa
        this.$router.push({ name: "ListaProjetos" });
      }
    },
    async avancar() {
      if (this.indiceEtapa < this.etapas.length - 1) {
        const proxima = this.etapas[this.indiceEtapa + 1].id;
        await this.navegarParaEtapa(proxima);
      }
    },
    async navegarParaEtapa(novaEtapa) {
      if (!novaEtapa || novaEtapa === this.etapaAtual) {
        return;
      }
      if (this.salvandoEtapa) {
        this.$toasted &&
          this.$toasted.info(
            "Aguarde o salvamento automático terminar antes de mudar de etapa."
          );
        return;
      }
      const etapaExiste = this.etapas.some((etapa) => etapa.id === novaEtapa);
      if (!etapaExiste) {
        return;
      }
      const salvou = await this.salvarEtapaAtual();
      if (!salvou) {
        return;
      }
      this.etapaAtual = novaEtapa;
      this.rolarTopo();
      if (!this.emEdicao && !this.projetoPersistidoId) {
        this.salvarRascunho();
      }
    },
    coletarErrosPesos() {
      const erros = [];
      this.form.acoes.forEach((acao) => {
        const entregas = Array.isArray(acao.entregas) ? acao.entregas : [];
        if (entregas.length) {
          const somaEntregas = entregas.reduce(
            (total, entrega) =>
              total + this.normalizarPercentualEntrada(entrega.peso),
            0
          );
          if (Math.abs(somaEntregas - 100) > 0.01) {
            const tituloAcao = acao.titulo || "Ação sem título";
            erros.push(
              `A soma dos pesos das entregas da ação "${tituloAcao}" deve totalizar 100%.`
            );
          }
        }

        entregas.forEach((entrega) => {
          const tarefas = Array.isArray(entrega.tarefas) ? entrega.tarefas : [];
          if (!tarefas.length) return;
          const somaTarefas = tarefas.reduce(
            (total, tarefa) =>
              total + this.normalizarPercentualEntrada(tarefa.peso),
            0
          );
          if (Math.abs(somaTarefas - 100) > 0.01) {
            const tituloEntrega = entrega.titulo || "Entrega sem título";
            erros.push(
              `A soma dos pesos das tarefas da entrega "${tituloEntrega}" deve totalizar 100%.`
            );
          }
        });
      });
      return erros;
    },
    deveSalvarEtapa(etapa) {
      switch (etapa) {
        case "basico":
          return Boolean(
            this.form.titulo ||
              this.datas.dataInicio ||
              this.datas.dataFim ||
              this.form.descricao
          );
        case "recursos":
          return (
            this.form.equipes.length > 0 || this.form.orcamentos.length > 0
          );
        case "metricas":
          return this.form.metricas.length > 0;
        case "acoes":
          return this.form.acoes.length > 0;
        default:
          return false;
      }
    },
    async salvarEtapaAtual() {
      const etapa = this.etapaAtual;
      if (!this.deveSalvarEtapa(etapa)) {
        this.errosPesos = [];
        return true;
      }

      if (etapa === "basico" && !this.form.titulo) {
        return true;
      }

      if (etapa === "basico" && !this.validarPeriodoPrincipal()) {
        return false;
      }

      if (etapa === "acoes") {
        const erros = this.coletarErrosPesos();
        this.errosPesos = erros;
        if (erros.length) {
          this.definirAlerta(
            "erro",
            "Ajuste os pesos das entregas e tarefas para totalizar 100%."
          );
          this.$toasted &&
            this.$toasted.error(
              "Ajuste os pesos das entregas e tarefas para totalizar 100%."
            );
          return false;
        }
      } else {
        this.errosPesos = [];
      }

      if (!this.alterado && (this.idProjetoAtivo || this.emEdicao)) {
        return true;
      }

      if (!this.validarReducaoAcoesSemExclusaoExplicita()) {
        return false;
      }

      try {
        this.salvandoEtapa = true;
        const payload = this.montarPayload();
        let resposta;
        const idProjeto = this.idProjetoAtivo;
        if (idProjeto) {
          resposta = await atualizarProjeto(idProjeto, payload);
        } else {
          resposta = await criarProjeto(payload);
        }
        const corpo = resposta?.data || {};
        const idExtraido = this.extrairIdProjeto(corpo);
        if (idExtraido) {
          this.projetoPersistidoId = idExtraido;
          this.emEdicao = true;
          this.limparRascunho();
        }
        const detalhe = corpo?.detalhe || corpo;
        if (detalhe && typeof detalhe === "object") {
          await this.popularFormularioComDetalhe(detalhe, {
            manterEtapa: true,
          });
        }
        this.alterado = false;
        this.alteracoesNaoAutosalvas = false;
        return true;
      } catch (error) {
        if (await this.tratarConflitoProjeto(error)) {
          return false;
        }
        this.definirAlerta(
          "erro",
          "Não foi possível salvar automaticamente a etapa atual."
        );
        this.$toasted &&
          this.$toasted.error(
            "Não foi possível salvar automaticamente a etapa atual."
          );
        // eslint-disable-next-line no-console
        console.error("[gestao-projetos] salvar-etapa", error);
        return false;
      } finally {
        this.salvandoEtapa = false;
      }
    },
    montarPayload() {
      const identificarMetricaParaPayload = (metrica) => {
        if (!metrica) return null;
        return this.normalizarIdentificador(
          metrica.idMetrica ?? metrica.id ?? metrica.__id ?? metrica.nome
        );
      };

      const acoes = this.form.acoes.map((acao) => {
        const metricasDetalhadas = (acao.metricasVinculadas || [])
          .map((referencia) => {
            if (!referencia) return null;
            const referenciaNormalizada =
              this.normalizarIdentificador(referencia);
            const metrica = this.form.metricas.find(
              (item) =>
                identificarMetricaParaPayload(item) ===
                  referenciaNormalizada ||
                String(identificarMetricaParaPayload(item)) ===
                  String(referencia).trim()
            );
            const idMetrica = this.normalizarIdentificador(
              metrica?.idMetrica ?? metrica?.id ?? referencia
            );
            return {
              idMetrica: typeof idMetrica === "number" ? idMetrica : null,
              referencia: String(referencia),
              nome: metrica?.nome || "",
            };
          })
          .filter(Boolean);
        return {
          idAcao: acao.idAcao || acao.idMarco,
          idMarco: acao.idMarco,
          titulo: acao.titulo,
          descricao: acao.descricao,
          dataInicio: this.converterDataParaIso(acao.dataInicio) || null,
          dataFim: this.converterDataParaIso(acao.dataFim) || null,
          dataLimite: this.converterDataParaIso(acao.dataLimite) || null,
          situacao: acao.situacao,
          progresso: acao.progresso,
          metricas: metricasDetalhadas,
          metricasIds: metricasDetalhadas
            .map((item) => item.idMetrica)
            .filter(Boolean),
          entregas: (acao.entregas || []).map((entrega) => ({
            idEntrega: entrega.idEntrega,
            idMarco: entrega.idMarco || acao.idMarco || acao.idAcao,
            titulo: entrega.titulo,
            descricao: entrega.descricao,
            idUsuarioResponsavel: entrega.idUsuarioResponsavel,
            dataLimite: this.converterDataParaIso(entrega.dataLimite) || null,
            dataConclusao:
              this.converterDataParaIso(entrega.dataConclusao) || null,
            situacao: entrega.situacao,
            percentualConclusao: this.normalizarPercentualEntrada(
              entrega.percentualConclusao
            ),
            peso: this.normalizarPercentualEntrada(entrega.peso),
            tarefas: (entrega.tarefas || []).map((tarefa) => ({
              idTarefa: tarefa.idTarefa,
              titulo: tarefa.titulo,
              descricao: tarefa.descricao,
              idUsuarioResponsavel: tarefa.idUsuarioResponsavel,
              dataLimite: this.converterDataParaIso(tarefa.dataLimite) || null,
              situacao: tarefa.situacao,
              percentualConclusao: this.normalizarPercentualEntrada(
                tarefa.percentualConclusao
              ),
              peso: this.normalizarPercentualEntrada(tarefa.peso),
            })),
          })),
        };
      });

      const idPdmNormalizado = this.normalizarIdentificador(this.form.idPdm);
      const idsEixosSelecionados = Array.isArray(this.form.idsEixosPdm)
        ? this.form.idsEixosPdm
            .map((id) => this.normalizarIdentificador(id))
            .filter((id) => id !== null && id !== undefined)
        : [];

      return {
        versao: this.versaoProjeto,
        origem: "salvamento-explicito-assistente-projeto",
        titulo: this.form.titulo,
        descricao: this.form.descricao,
        idPdm: idPdmNormalizado ?? null,
        idsEixosPdm: idsEixosSelecionados,
        idEixoEstrategico: idsEixosSelecionados[0] || null,
        idMunicipio: this.form.idMunicipio,
        dataInicio: this.form.dataInicio,
        dataFim: this.form.dataFim,
        situacao: this.form.situacao,
        prioridade: this.form.prioridade,
        nivelSaude: this.form.nivelSaude,
        orcamentoPlanejado: this.form.orcamentoPlanejado,
        orcamentoExecutado: this.form.orcamentoExecutado,
        objetivos: this.form.objetivos,
        escopo: this.form.escopo,
        acoes,
        marcos: acoes,
        equipes: this.form.equipes.map((membro) => ({
          idMembro: membro.idMembro,
          idUsuario: membro.idUsuario,
          nomeUsuario: membro.nomeUsuario,
          emailUsuario: membro.emailUsuario,
          telefoneUsuario: membro.telefoneUsuario,
          idInstituicao: membro.idInstituicao,
          nomeInstituicao: membro.instituicaoNome,
          papel: membro.papel,
          responsabilidades: membro.responsabilidades,
          alocacaoPercentual: membro.alocacaoPercentual,
        })),
        orcamentos: this.form.orcamentos.map((orcamento) => ({
          idOrcamento: orcamento.idOrcamento,
          categoria: orcamento.categoria,
          valorPlanejado: orcamento.valorPlanejado,
          valorExecutado: orcamento.valorExecutado,
        })),
        metricas: this.form.metricas.map((metrica) => ({
          idMetrica: metrica.idMetrica || metrica.id,
          tipo: metrica.tipo,
          nome: metrica.nome,
          descricao: metrica.descricao,
          linhaBase: metrica.linhaBase,
          meta: metrica.meta,
          valorAtual: metrica.valorAtual,
        })),
      };
    },
    formatarValor(valor) {
      if (valor === null || valor === undefined) return "R$ 0,00";
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(valor);
    },
    obterChaveMoeda(objeto, campo) {
      if (!objeto || objeto === this.form) {
        return `form:${campo}`;
      }
      if (objeto.__id) {
        return `${campo}:${objeto.__id}`;
      }
      if (objeto.idOrcamento) {
        return `${campo}:orcamento:${objeto.idOrcamento}`;
      }
      return `${campo}`;
    },
    obterValorMoeda(objeto, campo) {
      const chave = this.obterChaveMoeda(objeto, campo);
      if (
        Object.prototype.hasOwnProperty.call(this.valoresMoedaEmEdicao, chave)
      ) {
        return this.valoresMoedaEmEdicao[chave];
      }
      const valorAtual = objeto?.[campo];
      return this.formatarValorEditavel(valorAtual);
    },
    formatarValorEditavel(valor) {
      if (valor === null || valor === undefined || valor === "") {
        return "";
      }
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(Number(valor));
    },
    formatarValorParaEdicao(valor) {
      if (valor === null || valor === undefined || valor === "") {
        return "";
      }
      const numero = Number(valor);
      if (Number.isNaN(numero)) {
        return String(valor);
      }
      return numero.toFixed(2).replace(".", ",");
    },
    normalizarValorMoeda(valor) {
      if (valor === null || valor === undefined) return null;
      const somenteNumeros = String(valor)
        .replace(/[^\d,\.]/g, "")
        .replace(/\.(?=\d{3})/g, "");
      const comPonto = somenteNumeros.replace(/,/g, ".").trim();
      if (!comPonto) {
        return null;
      }
      const numero = Number(comPonto);
      return Number.isNaN(numero) ? null : Number(numero.toFixed(2));
    },
    manipularEntradaMoeda(evento, campo, objeto = this.form) {
      if (!evento || !evento.target) return;
      const texto = String(evento.target.value || "");
      const chave = this.obterChaveMoeda(objeto, campo);
      this.$set(this.valoresMoedaEmEdicao, chave, texto);
      const valorNormalizado = this.normalizarValorMoeda(texto);
      objeto[campo] = valorNormalizado;
      this.registrarAlteracao();
    },
    iniciarEdicaoMoeda(evento, campo, objeto = this.form) {
      if (!evento || !evento.target) return;
      const chave = this.obterChaveMoeda(objeto, campo);
      const texto = this.formatarValorParaEdicao(objeto?.[campo]);
      this.$set(this.valoresMoedaEmEdicao, chave, texto);
      evento.target.value = texto;
      this.$nextTick(() => {
        evento.target.select?.();
      });
    },
    finalizarEdicaoMoeda(evento, campo, objeto = this.form) {
      if (!evento || !evento.target) return;
      const chave = this.obterChaveMoeda(objeto, campo);
      const valorAtual = objeto?.[campo];
      if (
        Object.prototype.hasOwnProperty.call(this.valoresMoedaEmEdicao, chave)
      ) {
        this.$delete(this.valoresMoedaEmEdicao, chave);
      }
      evento.target.value = this.formatarValorEditavel(valorAtual);
    },
    normalizarPercentualEntrada(valor) {
      if (valor === null || valor === undefined || valor === "") {
        return 0;
      }
      const numero = Number(valor);
      if (Number.isNaN(numero)) {
        return 0;
      }
      const limitado = Math.min(100, Math.max(0, numero));
      return Number(limitado.toFixed(2));
    },
    extrairIdProjeto(dados) {
      if (!dados || typeof dados !== "object") return null;
      const candidatos = [
        dados.idProjeto,
        dados.id_projeto,
        dados?.detalhe?.idProjeto,
        dados?.detalhe?.id_projeto,
        dados?.projeto?.idProjeto,
        dados?.projeto?.id_projeto,
      ];
      for (const candidato of candidatos) {
        if (candidato !== null && candidato !== undefined) {
          const numero = Number(candidato);
          if (!Number.isNaN(numero)) {
            return numero;
          }
        }
      }
      return null;
    },
    extrairVersaoProjeto(dados) {
      if (!dados || typeof dados !== "object") return null;
      const candidatos = [
        dados.versao,
        dados.versaoProjeto,
        dados.revisao,
        dados.rowVersion,
        dados?.detalhe?.versao,
        dados?.detalhe?.versaoProjeto,
        dados?.detalhe?.revisao,
        dados?.detalhe?.rowVersion,
        dados?.projeto?.versao,
        dados?.projeto?.versaoProjeto,
        dados?.projeto?.revisao,
        dados?.projeto?.rowVersion,
      ];
      const versao = candidatos.find(
        (item) => item !== null && item !== undefined && item !== ""
      );
      return versao === undefined ? null : versao;
    },
    validarReducaoAcoesSemExclusaoExplicita() {
      if (!this.emEdicao || !this.idProjetoAtivo) return true;
      const quantidadeAtual = this.form.acoes.filter(
        (acao) => acao.idAcao || acao.idMarco
      ).length;
      if (quantidadeAtual >= this.quantidadeAcoesPersistidas) return true;

      const mensagem =
        "O salvamento foi bloqueado porque a quantidade de ações diminuiu sem uma exclusão explícita confirmada. Recarregue o projeto e revise as ações.";
      this.definirAlerta("erro", mensagem);
      this.$toasted && this.$toasted.error(mensagem);
      return false;
    },
    etapaConcluida(idEtapa) {
      switch (idEtapa) {
        case "basico":
          return Boolean(this.form.titulo);
        case "acoes":
          return this.form.acoes.length > 0;
        case "recursos":
          return (
            this.form.equipes.length > 0 || this.form.orcamentos.length > 0
          );
        case "metricas":
          return this.form.metricas.length > 0;
        case "revisao":
          return this.etapaAtual === "revisao";
        default:
          return false;
      }
    },
    salvarRascunho() {
      if (this.emEdicao) return;
      try {
        const payload = {
          etapaAtual: this.etapaAtual,
          form: this.form,
          datas: this.datas,
        };
        localStorage.setItem(this.chaveRascunho, JSON.stringify(payload));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("[gestao-projetos] salvar-rascunho", error);
      }
    },
    async carregarRascunho() {
      if (this.emEdicao) return;
      try {
        const salvo = localStorage.getItem(this.chaveRascunho);
        if (!salvo) return;
        const dados = JSON.parse(salvo);
        if (!dados || !dados.form) return;
        this.valoresMoedaEmEdicao = {};
        const metricasRascunho = (dados.form.metricas || []).map((item) => ({
          ...item,
          __id: item.__id || uid(),
        }));
        const mapaMetricas = new Map(
          metricasRascunho.map((metrica) => [
            this.identificadorMetrica(metrica),
            metrica,
          ])
        );
        const acoesRascunho = (dados.form.acoes || dados.form.marcos || []).map(
          (acao) => {
            const metricasRelacionadas = this.normalizarMetricasDaAcao(
              acao.metricas ||
                acao.metricasVinculadas ||
                acao.metricasIds ||
                [],
              mapaMetricas
            );
            metricasRelacionadas.forEach((metrica) => {
              const chave = this.identificadorMetrica(metrica);
              if (chave && !mapaMetricas.has(chave)) {
                mapaMetricas.set(chave, {
                  ...metrica,
                  __id: metrica.__id || uid(),
                });
              }
            });
            const metricasVinculadas = metricasRelacionadas
              .map((metrica) => this.identificadorMetrica(metrica))
              .filter(Boolean);
            return {
              ...acao,
              idAcao: acao.idAcao || acao.idMarco,
              __id: acao.__id || uid(),
              dataInicio: this.formatarDataBr(acao.dataInicio),
              dataFim: this.formatarDataBr(acao.dataFim),
              dataLimite: this.formatarDataBr(acao.dataLimite),
              __progressoManual: Boolean(acao.__progressoManual),
              __colapsada: acao.__colapsada === true,
              metricasVinculadas,
              __metricasVinculadasConfirmadas: [...metricasVinculadas],
              entregas: (acao.entregas || []).map((entrega) => ({
                ...entrega,
                __id: entrega.__id || uid(),
                responsavelNome:
                  entrega.responsavelNome || entrega.responsavel || "",
                responsavelEmail:
                  entrega.responsavelEmail || entrega.emailResponsavel || "",
                dataLimite: this.formatarDataBr(entrega.dataLimite),
                dataConclusao: this.formatarDataBr(entrega.dataConclusao),
                percentualConclusao: this.normalizarPercentualEntrada(
                  entrega.percentualConclusao
                ),
                peso: this.normalizarPercentualEntrada(entrega.peso),
                __progressoManual: Boolean(entrega.__progressoManual),
                __colapsada: entrega.__colapsada === true,
                tarefas: (entrega.tarefas || []).map((tarefa) => ({
                  ...tarefa,
                  __id: tarefa.__id || uid(),
                  responsavelNome:
                    tarefa.responsavelNome || tarefa.responsavel || "",
                  responsavelEmail:
                    tarefa.responsavelEmail || tarefa.emailResponsavel || "",
                  dataLimite: this.formatarDataBr(tarefa.dataLimite),
                  percentualConclusao: this.normalizarPercentualEntrada(
                    tarefa.percentualConclusao
                  ),
                  peso: this.normalizarPercentualEntrada(tarefa.peso),
                })),
              })),
            };
          }
        );

        const idPdmRascunho = this.normalizarIdentificador(
          dados.form.idPdm ??
            dados.form.idPlanoMunicipal ??
            dados.form.idPlano ??
            dados.form.pdmId
        );
        const idsEixosRascunho = this.extrairIdsEixos(dados.form);

        this.form = {
          ...this.estadoInicial(),
          ...dados.form,
          idPdm: idPdmRascunho,
          nomePdm: dados.form?.nomePdm || dados.form?.nomePlanoMunicipal || "",
          idsEixosPdm: idsEixosRascunho,
          acoes: acoesRascunho,
          equipes: (dados.form.equipes || []).map((membro) => ({
            ...membro,
            __id: membro.__id || uid(),
            nomeUsuario: membro.nomeUsuario || membro.nome || "",
            emailUsuario: membro.emailUsuario || membro.email || "",
            telefoneUsuario: this.aplicarMascaraTelefone(
              membro.telefoneUsuario || membro.telefone || ""
            ),
            idInstituicao:
              membro.idInstituicao || membro.instituicaoId || undefined,
            instituicaoNome:
              membro.instituicaoNome ||
              membro.nomeInstituicao ||
              membro.instituicao ||
              "",
          })),
          orcamentos: (dados.form.orcamentos || []).map((item) => ({
            ...item,
            __id: item.__id || uid(),
          })),
          metricas: Array.from(mapaMetricas.values()),
        };
        this.form.dataInicio = this.converterDataParaIso(this.form.dataInicio);
        this.form.dataFim = this.converterDataParaIso(this.form.dataFim);
        this.datas = {
          dataInicio: this.formatarDataBr(
            dados.datas?.dataInicio || this.form.dataInicio
          ),
          dataFim: this.formatarDataBr(
            dados.datas?.dataFim || this.form.dataFim
          ),
        };
        this.erros.periodoPrincipal = false;
        if (dados.etapaAtual) {
          this.etapaAtual = dados.etapaAtual;
        }
        this.municipioSelecionadoNome = this.form.nomeMunicipio || "";
        await this.carregarPdmsDisponiveis();
        if (this.form.idPdm) {
          await this.carregarEixosDoPdm(this.form.idPdm);
        }
        this.$nextTick(() => {
          this.tentarSincronizarMunicipio();
          this.prepararProgressosIniciais();
        });
        this.alterado = true;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("[gestao-projetos] carregar-rascunho", error);
      }
    },
    limparRascunho() {
      clearTimeout(this.rascunhoTimeout);
      localStorage.removeItem(this.chaveRascunho);
    },
    prevenirSaida(evento) {
      if (!this.deveAlertarSaida) return;
      evento.preventDefault();
      evento.returnValue = "";
    },
    definirMembro(indice, usuario) {
      const membro = this.form.equipes[indice];
      if (!membro) return;
      membro.idUsuario =
        usuario?.usuarioId || usuario?.idUsuario || usuario?.id || null;
      const nome = usuario?.nome || usuario?.email || "";
      membro.nomeUsuario = nome || membro.nomeUsuario || "";
      membro.emailUsuario = usuario?.email || membro.emailUsuario || "";
      membro.telefoneUsuario = this.aplicarMascaraTelefone(
        usuario?.telefone || membro.telefoneUsuario || ""
      );
      this.registrarAlteracao();
    },
    definirInstituicaoMembro(indice, instituicao) {
      const membro = this.form.equipes[indice];
      if (!membro) return;
      membro.idInstituicao =
        instituicao?.idInstituicao ||
        instituicao?.instituicaoid ||
        instituicao?.id ||
        null;
      membro.instituicaoNome =
        instituicao?.nome || instituicao?.descricao || "";
      this.registrarAlteracao();
    },
    definirResponsavelEntrega(indiceAcao, indiceEntrega, usuario) {
      const acao = this.form.acoes[indiceAcao];
      if (!acao || !Array.isArray(acao.entregas)) return;
      const entrega = acao.entregas[indiceEntrega];
      if (!entrega) return;
      entrega.idUsuarioResponsavel =
        usuario?.usuarioId || usuario?.idUsuario || usuario?.id || null;
      entrega.responsavelNome = usuario?.nome || usuario?.email || "";
      entrega.responsavelEmail = usuario?.email || "";
      this.registrarAlteracao();
    },
    definirResponsavelTarefa(indiceAcao, indiceEntrega, indiceTarefa, usuario) {
      const acao = this.form.acoes[indiceAcao];
      if (!acao || !Array.isArray(acao.entregas)) return;
      const entrega = acao.entregas[indiceEntrega];
      if (!entrega || !Array.isArray(entrega.tarefas)) return;
      const tarefa = entrega.tarefas[indiceTarefa];
      if (!tarefa) return;
      tarefa.idUsuarioResponsavel =
        usuario?.usuarioId || usuario?.idUsuario || usuario?.id || null;
      tarefa.responsavelNome = usuario?.nome || usuario?.email || "";
      tarefa.responsavelEmail = usuario?.email || "";
      this.registrarAlteracao();
    },
    limitarPercentual(objeto, campo) {
      if (!objeto || !campo) return;
      const valorNormalizado = this.normalizarPercentualEntrada(objeto[campo]);
      objeto[campo] = valorNormalizado;
    },
    calcularProgressoPonderado(itens, campoValor, campoPeso) {
      if (!Array.isArray(itens) || !itens.length) {
        return null;
      }
      let somaPonderada = 0;
      let somaPesos = 0;
      let somaSemPeso = 0;
      let contadorSemPeso = 0;
      itens.forEach((item) => {
        if (!item) return;
        const progresso = this.normalizarPercentualEntrada(item[campoValor]);
        const peso = this.normalizarPercentualEntrada(item[campoPeso]);
        if (peso > 0) {
          somaPesos += peso;
          somaPonderada += progresso * peso;
        } else {
          somaSemPeso += progresso;
          contadorSemPeso += 1;
        }
      });
      if (somaPesos > 0) {
        return Number((somaPonderada / somaPesos).toFixed(2));
      }
      if (contadorSemPeso > 0) {
        return Number((somaSemPeso / contadorSemPeso).toFixed(2));
      }
      return 0;
    },
    calcularProgressoEntrega(entrega) {
      if (!entrega) return 0;
      const tarefas = Array.isArray(entrega.tarefas) ? entrega.tarefas : [];
      if (!tarefas.length) {
        return this.normalizarPercentualEntrada(entrega.percentualConclusao);
      }
      return this.calcularProgressoPonderado(
        tarefas,
        "percentualConclusao",
        "peso"
      );
    },
    calcularProgressoAcao(acao) {
      if (!acao) return 0;
      const entregas = Array.isArray(acao.entregas) ? acao.entregas : [];
      if (!entregas.length) {
        return this.normalizarPercentualEntrada(acao.progresso);
      }
      return this.calcularProgressoPonderado(
        entregas,
        "percentualConclusao",
        "peso"
      );
    },
    prepararProgressosIniciais(acoes = this.form.acoes) {
      if (!Array.isArray(acoes)) return;
      acoes.forEach((acao) => {
        if (!acao) return;
        if (typeof acao.__colapsada !== "boolean") {
          this.$set(acao, "__colapsada", false);
        }
        if (!Array.isArray(acao.entregas)) {
          return;
        }
        acao.entregas.forEach((entrega) => {
          if (!entrega) return;
          if (typeof entrega.__colapsada !== "boolean") {
            this.$set(entrega, "__colapsada", false);
          }
          const calculado = this.calcularProgressoEntrega(entrega);
          const atual = this.normalizarPercentualEntrada(
            entrega.percentualConclusao
          );
          if (
            Array.isArray(entrega.tarefas) &&
            entrega.tarefas.length &&
            Math.abs(calculado - atual) <= 0.01
          ) {
            entrega.__progressoManual = false;
            entrega.percentualConclusao = calculado;
          } else if (
            entrega.__progressoManual !== true &&
            (!Array.isArray(entrega.tarefas) || !entrega.tarefas.length)
          ) {
            entrega.__progressoManual = true;
            entrega.percentualConclusao = atual;
          } else {
            entrega.__progressoManual =
              entrega.__progressoManual === true ||
              Math.abs(calculado - atual) > 0.01;
            if (!entrega.__progressoManual) {
              entrega.percentualConclusao = calculado;
            }
          }
        });
        const calculadoAcao = this.calcularProgressoAcao(acao);
        const atualAcao = this.normalizarPercentualEntrada(acao.progresso);
        if (
          acao.entregas.length &&
          Math.abs(calculadoAcao - atualAcao) <= 0.01
        ) {
          acao.__progressoManual = false;
          acao.progresso = calculadoAcao;
        } else {
          acao.__progressoManual =
            acao.__progressoManual === true ||
            Math.abs(calculadoAcao - atualAcao) > 0.01;
          if (!acao.__progressoManual) {
            acao.progresso = calculadoAcao;
          }
        }
      });
    },
    recalcularProgressoEntrega(acao, entrega) {
      if (!entrega) return;
      const valor = this.calcularProgressoEntrega(entrega);
      if (valor === null || Number.isNaN(valor)) return;
      entrega.percentualConclusao = this.normalizarPercentualEntrada(valor);
      if (acao && !acao.__progressoManual) {
        const calculadoAcao = this.calcularProgressoAcao(acao);
        if (calculadoAcao !== null && !Number.isNaN(calculadoAcao)) {
          acao.progresso = this.normalizarPercentualEntrada(calculadoAcao);
        }
      }
    },
    recalcularProgressoAcao(acao) {
      if (!acao) return;
      const valor = this.calcularProgressoAcao(acao);
      if (valor === null || Number.isNaN(valor)) return;
      acao.progresso = this.normalizarPercentualEntrada(valor);
    },
    aoAlterarTarefa(indiceAcao, indiceEntrega) {
      const acao = this.form.acoes[indiceAcao];
      if (!acao || !Array.isArray(acao.entregas)) return;
      const entrega = acao.entregas[indiceEntrega];
      if (!entrega) return;
      if (!entrega.__progressoManual) {
        this.recalcularProgressoEntrega(acao, entrega);
      } else if (!acao.__progressoManual) {
        const calculadoAcao = this.calcularProgressoAcao(acao);
        if (calculadoAcao !== null && !Number.isNaN(calculadoAcao)) {
          acao.progresso = this.normalizarPercentualEntrada(calculadoAcao);
        }
      }
      this.registrarAlteracao();
    },
    aoAlterarEntrega(indiceAcao, indiceEntrega) {
      const acao = this.form.acoes[indiceAcao];
      if (!acao || !Array.isArray(acao.entregas)) return;
      const entrega = acao.entregas[indiceEntrega];
      if (!entrega) return;
      if (!entrega.__progressoManual) {
        this.recalcularProgressoEntrega(acao, entrega);
      } else if (!acao.__progressoManual) {
        const calculado = this.calcularProgressoAcao(acao);
        if (calculado !== null && !Number.isNaN(calculado)) {
          acao.progresso = this.normalizarPercentualEntrada(calculado);
        }
      }
      this.registrarAlteracao();
    },
    marcarProgressoEntregaManual(entrega, acao) {
      if (!entrega) return;
      entrega.__progressoManual = true;
      this.limitarPercentual(entrega, "percentualConclusao");
      if (acao && !acao.__progressoManual) {
        const calculado = this.calcularProgressoAcao(acao);
        if (calculado !== null && !Number.isNaN(calculado)) {
          acao.progresso = this.normalizarPercentualEntrada(calculado);
        }
      }
      this.registrarAlteracao();
    },
    marcarProgressoAcaoManual(acao) {
      if (!acao) return;
      acao.__progressoManual = true;
      this.limitarPercentual(acao, "progresso");
      this.registrarAlteracao();
    },
    restaurarProgressoEntrega(acao, entrega) {
      if (!entrega) return;
      entrega.__progressoManual = false;
      this.recalcularProgressoEntrega(acao, entrega);
      this.registrarAlteracao();
    },
    restaurarProgressoAcao(acao) {
      if (!acao) return;
      acao.__progressoManual = false;
      this.recalcularProgressoAcao(acao);
      this.registrarAlteracao();
    },
    async salvar() {
      this.definirAlerta("", "");

      // Valida título
      this.validarTitulo();

      // Verifica se há erros específicos nos campos
      const temErros =
        this.erros.titulo || this.erros.dataInicio || this.erros.dataFim;

      if (!this.form.titulo || temErros) {
        this.definirAlerta(
          "erro",
          "Corrija os erros destacados antes de salvar."
        );
        this.$toasted &&
          this.$toasted.error("Corrija os erros destacados antes de salvar.");
        return;
      }
      if (!this.validarPeriodoPrincipal()) {
        this.$toasted &&
          this.$toasted.error("Ajuste o período do projeto antes de salvar.");
        this.etapaAtual = "basico";
        return;
      }
      if (!this.validarReducaoAcoesSemExclusaoExplicita()) {
        return;
      }
      try {
        this.salvando = true;
        const payload = this.montarPayload();
        let resposta;
        const idProjetoAtual = this.idProjetoAtivo;
        if (idProjetoAtual) {
          resposta = await atualizarProjeto(idProjetoAtual, payload);
        } else {
          resposta = await criarProjeto(payload);
        }
        const corpo = resposta?.data || {};
        const idProjeto = this.extrairIdProjeto(corpo) || idProjetoAtual;
        const versao = this.extrairVersaoProjeto(corpo);
        if (versao !== null) {
          this.versaoProjeto = versao;
        }
        this.$toasted && this.$toasted.success("Projeto salvo com sucesso!");
        this.definirAlerta("sucesso", "Projeto salvo com sucesso!");
        this.alterado = false;
        this.alteracoesNaoAutosalvas = false;
        this.quantidadeAcoesPersistidas = this.form.acoes.filter(
          (acao) => acao.idAcao || acao.idMarco
        ).length;
        this.limparRascunho();
        if (idProjeto) {
          this.projetoPersistidoId = idProjeto;
        }
        this.emEdicao = true;
        this.$router.push({
          name: "gestao-projetos.detalhe",
          params: { idProjeto: idProjeto || this.idProjetoAtivo },
        });
      } catch (error) {
        if (await this.tratarConflitoProjeto(error)) {
          return;
        }
        this.definirAlerta("erro", "Não foi possível salvar o projeto.");
        this.$toasted &&
          this.$toasted.error("Não foi possível salvar o projeto.");
        // eslint-disable-next-line no-console
        console.error("[gestao-projetos] salvar", error);
      } finally {
        this.salvando = false;
      }
    },
  },

  isValidDateISO(dateString) {
    if (!dateString || typeof dateString !== "string") return false;
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(dateString)) return false;
    const date = new Date(dateString);
    return (
      date instanceof Date &&
      !isNaN(date) &&
      dateString === date.toISOString().split("T")[0]
    );
  },
};
</script>

<style scoped lang="scss">
.assistente-projeto {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.alerta {
  border-radius: 10px;
  padding: 10px 14px;
  border: 1px solid #c7d2e2;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 0.9rem;
}

.alerta[data-tipo="erro"] {
  background: #fee2e2;
  border-color: #fecaca;
  color: #b91c1c;
}

.alerta[data-tipo="sucesso"] {
  background: #dcfce7;
  border-color: #bbf7d0;
  color: #166534;
}

form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

header h2 {
  margin: 0;
  font-size: 1.8rem;
  color: #0f172a;
}

.coluna-cheia {
  grid-column: 1 / -1;
}

.etapas-container {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.etapas {
  display: flex;
  position: relative;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.etapas button {
  position: relative;
  background: #ffffff;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 180px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
  z-index: 2;
}

.etapas button:hover {
  border-color: #0074cc;
  box-shadow: 0 4px 12px rgba(0, 116, 204, 0.15);
  transform: translateY(-2px);
}

.etapas button.ativo {
  background: linear-gradient(135deg, #0074cc 0%, #0056b3 100%);
  border-color: #0074cc;
  color: #ffffff;
  box-shadow: 0 8px 20px rgba(0, 116, 204, 0.3);
  transform: translateY(-2px);
}

.etapas button.concluida:not(.ativo) {
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border-color: #10b981;
  color: #065f46;
}

.etapas button.concluida:not(.ativo):hover {
  border-color: #059669;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.15);
}

.etapa-numero {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f3f4f6;
  color: #6b7280;
  font-weight: 700;
  font-size: 0.875rem;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.etapas button.ativo .etapa-numero {
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
}

.etapas button.concluida:not(.ativo) .etapa-numero {
  background: #10b981;
  color: #ffffff;
}

.etapa-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
}

.etapa-nome {
  font-weight: 600;
  font-size: 0.95rem;
  color: #374151;
  margin-bottom: 2px;
  line-height: 1.2;
}

.etapas button.ativo .etapa-nome {
  color: #ffffff;
}

.etapas button.concluida:not(.ativo) .etapa-nome {
  color: #065f46;
}

.etapa-status-texto {
  font-size: 0.75rem;
  font-weight: 500;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.etapas button.ativo .etapa-status-texto {
  color: rgba(255, 255, 255, 0.8);
}

.etapas button.concluida:not(.ativo) .etapa-status-texto {
  color: #047857;
}

.check-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Responsividade */
@media (max-width: 1024px) {
  .etapas {
    flex-direction: column;
    gap: 16px;
  }

  .etapas button {
    min-width: 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 768px) {
  .etapas-container {
    padding: 16px;
  }

  .etapas button {
    padding: 14px 16px;
    min-width: 100%;
  }

  .etapa-numero {
    width: 28px;
    height: 28px;
    font-size: 0.75rem;
  }

  .etapa-nome {
    font-size: 0.875rem;
  }

  .etapa-status-texto {
    font-size: 0.7rem;
  }
}

.secao-dados-basicos {
  background: transparent;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.secao-dados-metricas {
  background: transparent;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.secao-dados-metricas .secao-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
}

.secao-dados-metricas .header-content {
  flex: 1;
}

.metricas-lista {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.metrica-item {
  padding: 24px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.metrica-item:hover {
  border-color: #d1d5db;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.campo-grupo {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  grid-column: 1 / -1;
}

.input-with-icon {
  position: relative;
  display: flex;
  align-items: center;
}

.input-with-icon .input-icon {
  position: absolute;
  left: 12px;
  z-index: 1;
  color: #6b7280;
  pointer-events: none;
}

.input-with-icon-field {
  padding-left: 40px !important;
}

.input-with-icon:focus-within .input-icon {
  color: #0074cc;
}

.secao-header {
  padding: 24px 0;
  border-bottom: 1px solid #e2e8f0;
}

.header-content h3 {
  margin: 0 0 8px 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
}

.header-content p {
  margin: 0;
  color: #64748b;
  font-size: 1rem;
  line-height: 1.5;
}

.form-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.campo-principal {
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0;
  box-shadow: none;
}

.campos-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  width: 100%;
}

.campo {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.campo-titulo {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.label-text {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
}

.label-text svg {
  color: #0074cc;
  flex-shrink: 0;
}

.input-principal {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  font-size: 0.95rem;
  color: #1f2937;
  background: #ffffff;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.input-principal:focus {
  outline: none;
  border-color: #0074cc;
  box-shadow: 0 0 0 3px rgba(0, 116, 204, 0.1);
}

.input-principal::placeholder {
  color: #9ca3af;
}

.input-wrapper {
  position: relative;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #ffffff;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.input-wrapper:focus-within {
  border-color: #0074cc;
  box-shadow: 0 0 0 3px rgba(0, 116, 204, 0.1);
}

/* Wrapper para inputs de data com calendário */
.input-wrapper-date {
  position: relative;
  display: flex;
  align-items: center;
}

.input-wrapper-date .date-field {
  padding-right: 45px; /* Espaço para o ícone */
}

.date-picker-hidden {
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 0;
  height: 0;
}

.calendar-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.calendar-icon:hover {
  color: #374151;
  background: #f3f4f6;
}

.calendar-icon:focus {
  outline: none;
  color: #0074cc;
  background: #eff6ff;
}

/* Reset estilos dos componentes de busca para usar input-wrapper */
.input-wrapper .busca-usuario,
.input-wrapper .busca-instituicao {
  width: 100%;
}

.input-wrapper .busca-usuario .campo-input,
.input-wrapper .busca-instituicao .campo-input {
  border: none !important;
  border-radius: 0 !important;
  padding: 12px 16px !important;
  background: transparent !important;
  box-shadow: none !important;
  margin: 0 !important;
}

.input-wrapper .busca-usuario .campo-input input,
.input-wrapper .busca-instituicao .campo-input input {
  padding: 0 !important;
  margin: 0 !important;
  font-size: 0.95rem !important;
  color: #1f2937 !important;
}

.input-wrapper .busca-usuario .campo-input:focus-within,
.input-wrapper .busca-instituicao .campo-input:focus-within {
  border: none !important;
  box-shadow: none !important;
}

.select-field {
  width: 100%;
  padding: 12px 16px;
  padding-right: 40px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  font-size: 0.95rem;
  color: #1f2937;
  background: #ffffff;
  background-image: url('data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%23374151" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>');
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.input-field {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  font-size: 0.95rem;
  color: #1f2937;
  background: #ffffff;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.select-field:focus {
  outline: none;
  border-color: #0074cc;
  box-shadow: 0 0 0 3px rgba(0, 116, 204, 0.1);
  background-image: url('data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%230074cc" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>');
}

.input-field:focus {
  outline: none;
  border-color: #0074cc;
  box-shadow: 0 0 0 3px rgba(0, 116, 204, 0.1);
}

.select-multiple {
  min-height: 120px;
  background-image: none;
  padding-right: 16px;
}

.textarea-field {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  font-size: 0.95rem;
  color: #1f2937;
  background: #ffffff;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
  line-height: 1.5;
}

.textarea-field:focus {
  outline: none;
  border-color: #0074cc;
  box-shadow: 0 0 0 3px rgba(0, 116, 204, 0.1);
}

.textarea-field::placeholder {
  color: #9ca3af;
}

.campo-ajuda {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  margin-top: 4px;
  color: #6b7280;
}

.campo-ajuda.loading {
  color: #0074cc;
}

.campo-ajuda.warning {
  color: #d97706;
}

.campo-ajuda svg {
  flex-shrink: 0;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Estilos para seções de equipes e orçamento */
.secao-container {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.secao-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  border-bottom: 1px solid #e5e7eb;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-content h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.header-content svg {
  color: #0074cc;
  flex-shrink: 0;
}

.botao-adicionar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid #0074cc;
  border-radius: 8px;
  background: linear-gradient(135deg, #0074cc 0%, #005aa3 100%);
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 116, 204, 0.2);
}

.botao-adicionar:hover {
  background: linear-gradient(135deg, #005aa3 0%, #004080 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 116, 204, 0.3);
}

.botao-adicionar svg {
  flex-shrink: 0;
}

.estado-vazio {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  background: #f9fafb;
  border: 1px dashed #d1d5db;
  border-radius: 12px;
  color: #6b7280;
}

.estado-vazio svg {
  color: #9ca3af;
  margin-bottom: 16px;
}

.estado-vazio p {
  font-size: 1rem;
  font-weight: 500;
  color: #374151;
  margin: 0 0 8px 0;
}

.estado-vazio span {
  font-size: 0.9rem;
  color: #6b7280;
}

.membros-lista,
.orcamentos-lista {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.membro-item,
.orcamento-item {
  padding: 24px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.membro-item:hover,
.orcamento-item:hover {
  border-color: #d1d5db;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.item-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid #f3f4f6;
}

.botao-remover {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1px solid #dc2626;
  border-radius: 8px;
  background: #ffffff;
  color: #dc2626;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.botao-remover:hover {
  background: #dc2626;
  color: #ffffff;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(220, 38, 38, 0.3);
}

.botao-remover svg {
  flex-shrink: 0;
}

/* Estilos para combobox */
.campo :deep(.combobox-municipio .v-input__control),
.campo :deep(.combobox-municipio .v-field) {
  border-radius: 10px;
  border: none;
  background: transparent;
  box-shadow: none;
  transition: all 0.2s ease;
  min-height: 48px;
}

.campo :deep(.combobox-municipio .v-field:hover) {
  border: none;
  box-shadow: none;
}

.campo :deep(.combobox-municipio .v-field--focused) {
  border: none;
  box-shadow: none;
}

.campo :deep(.combobox-municipio .v-field__input) {
  padding: 12px 16px;
  font-size: 0.95rem;
}

/* Responsividade */
@media (max-width: 768px) {
  .campos-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .campo-principal {
    padding: 20px;
  }

  .input-principal {
    padding: 14px 16px;
    font-size: 1rem;
  }

  .header-content h3 {
    font-size: 1.3rem;
  }

  .form-content {
    gap: 20px;
  }
}

.secao-dados-acoes {
  background: transparent;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.secao-dados-acoes .cartao + .cartao {
  margin-top: 24px;
}

.guia-estrutura {
  background: linear-gradient(135deg, #e0f2fe 0%, #e0e7ff 100%);
  border: 1px solid #bfdbfe;
  border-radius: 12px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 0.85rem;
  color: #0f172a;
  margin-bottom: 10px;
}

.guia-estrutura h4 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #1d4ed8;
}

.guia-estrutura ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.guia-estrutura li {
  display: flex;
  align-items: center;
  gap: 10px;
  line-height: 1.4;
}

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
}

@media (max-width: 768px) {
  .grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

.contato-equipe {
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
}

.campo-com-acao {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.campo-com-acao input {
  flex: 1;
  min-width: 120px;
}

.acao-texto {
  border: 1px solid #3b82f6;
  background: #ffffff;
  color: #3b82f6;
  font-size: 0.875rem;
  font-weight: 500;
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
}

.acao-texto:hover:not(:disabled) {
  background: #3b82f6;
  color: #ffffff;
}

.acao-texto:disabled {
  color: #9ca3af;
  border-color: #d1d5db;
  cursor: not-allowed;
  opacity: 0.6;
}

label {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.85rem;
  color: #334155;
}

.ajuda {
  color: #64748b;
  font-size: 0.78rem;
}

input,
textarea,
select {
  border: 1px solid #c7d2e2;
  border-radius: 8px;
  padding: 8px 10px;
  font: inherit;
  color: #0f172a;
  background: #f8fafc;
}

.assistente-projeto.bloqueado {
  position: relative;
}

.overlay-salvando {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 60;
}

.overlay-conteudo {
  background: #ffffff;
  padding: 20px 28px;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.2);
  display: flex;
  align-items: center;
  gap: 12px;
}

.overlay-conteudo p {
  margin: 0;
  font-size: 0.95rem;
  color: #0f172a;
}

.spinner {
  width: 24px;
  height: 24px;
  border-radius: 999px;
  border: 3px solid #c7d2e2;
  border-top-color: #1d4ed8;
  animation: girar 0.8s linear infinite;
}

@keyframes girar {
  to {
    transform: rotate(360deg);
  }
}

textarea {
  min-height: 80px;
}

input.invalido,
textarea.invalido,
select.invalido {
  border-color: #f87171 !important;
}

/* Mensagens de erro embaixo dos campos */
.campo-erro {
  color: #dc2626;
  font-size: 0.875rem;
  font-weight: 500;
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.campo-erro::before {
  content: "⚠";
  color: #dc2626;
  font-size: 0.75rem;
}

.campo-municipio {
  gap: 6px;
}

:deep(.combobox-municipio) {
  width: 100%;
}

:deep(.combobox-municipio .v-field) {
  border: none;
  background: transparent;
  min-height: 44px;
  box-shadow: none;
}

:deep(.combobox-municipio .v-field__input),
:deep(.combobox-municipio input) {
  min-height: 44px;
  padding: 6px 12px;
}

/* Remove bordas de outros componentes de busca */
.campo :deep(.v-input__control),
.campo :deep(.v-field) {
  border: none;
  background: transparent;
  box-shadow: none;
}

.campo :deep(.v-field:hover),
.campo :deep(.v-field--focused) {
  border: none;
  box-shadow: none;
}

.date-field {
  background-image: none !important;
  padding-right: 16px !important;
  cursor: pointer;
}

.date-field::-webkit-calendar-picker-indicator {
  background: url('data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%23374151" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2v4m8-4v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/></svg>')
    no-repeat center;
  background-size: 16px;
  width: 16px;
  height: 16px;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.date-field::-webkit-calendar-picker-indicator:hover {
  opacity: 1;
}

.date-field:focus::-webkit-calendar-picker-indicator {
  background: url('data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%230074cc" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2v4m8-4v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/></svg>')
    no-repeat center;
  background-size: 16px;
}

.secao-cabecalho {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}

.secao-cabecalho button,
.secao button[type="button"]:not(.alternar-colapso):not(.perigo) {
  border-radius: 8px;
  border: 1px solid #3b82f6;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: #ffffff;
  padding: 8px 14px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
}

.secao-cabecalho button:hover,
.secao button[type="button"]:not(.alternar-colapso):not(.perigo):hover {
  background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
}

.secao-cabecalho.interno {
  padding: 12px 0;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 16px;
}

.secao-cabecalho.interno h4,
.secao-cabecalho.interno h5 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
}

.alternar-colapso {
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #374151;
  padding: 8px 14px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.alternar-colapso:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
  color: #1f2937;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.texto-ajuda-metricas {
  font-size: 0.85rem;
  color: #475569;
  line-height: 1.4;
}

.cartao {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.cartao:hover {
  border-color: #d1d5db;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.cartao.colapsado {
  background: #f9fafb;
  border-color: #e5e7eb;
}

.cartao-conteudo {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 16px;
}

.cartao-cabecalho {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.cartao-identificacao {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex: 1;
}

.cartao-textos {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.cartao-textos strong {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.4;
  word-break: break-word;
}

.cartao-textos small {
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.4;
}

.cartao-identificacao.tarefa {
  align-items: flex-start;
  gap: 16px;
  padding: 4px 0;
}

.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.badge-acao {
  background: #1d4ed8;
  color: #ffffff;
}

.badge-entrega {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.2);
  border: none;
}

.badge-tarefa {
  background: #38bdf8;
  color: #0f172a;
}

.cartao.interno {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
  margin: 0;
  box-shadow: none;
}

.cartao.interno:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.cartao.tarefa {
  border: 1px dashed #d1d5db;
  background: #fefefe;
  padding: 14px;
  margin: 8px 0;
}

.cartao.tarefa:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.vazio {
  text-align: center;
  color: #6b7280;
  border: 1px dashed #d1d5db;
  border-radius: 12px;
  padding: 24px 16px;
  background: #f9fafb;
  font-size: 0.9rem;
  font-style: italic;
}

.vazio.interno {
  padding: 20px 16px;
  margin: 8px 0;
  background: #f8fafc;
  border-color: #e2e8f0;
}

.perigo {
  border: 1px solid #ef4444;
  color: #dc2626;
  background: #ffffff;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-start;
  box-shadow: 0 1px 2px rgba(239, 68, 68, 0.1);
}

.perigo:hover {
  background: #ef4444;
  color: #ffffff;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(239, 68, 68, 0.2);
}

.resumo {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
}

.resumo li {
  background: #f8fafc;
  border: 1px solid #d6dde9;
  border-radius: 10px;
  padding: 10px;
}

.atalhos-navegacao {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.atalhos-navegacao button {
  border: 1px solid #c7d2e2;
  background: #ffffff;
  color: #1f2937;
  border-radius: 999px;
  padding: 6px 14px;
  cursor: pointer;
}

.acoes {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 24px 0;
  margin-top: 32px;
  border-top: 1px solid #e5e7eb;
}

.acoes button {
  border-radius: 12px;
  border: none;
  padding: 12px 24px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  overflow: hidden;
  min-width: 120px;
  justify-content: center;
}

.acoes button::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: left 0.5s;
}

.acoes button:hover::before {
  left: 100%;
}

.acoes button:not(.secundario) {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: #ffffff;
  box-shadow: 0 4px 14px 0 rgba(59, 130, 246, 0.25);
}

.acoes button:not(.secundario):hover {
  background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
  box-shadow: 0 6px 20px 0 rgba(59, 130, 246, 0.4);
  transform: translateY(-2px);
}

.acoes button:not(.secundario):active {
  transform: translateY(0);
  box-shadow: 0 2px 8px 0 rgba(59, 130, 246, 0.3);
}

.acoes .secundario {
  background: #ffffff;
  color: #6b7280;
  border: 1px solid #d1d5db !important;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
}

.acoes .secundario:hover {
  background: #f9fafb;
  color: #374151;
  border-color: #9ca3af !important;
  box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
}

.acoes .secundario:active {
  transform: translateY(0);
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
}

.acoes button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: none !important;
}

.acoes button:disabled::before {
  display: none;
}

.acoes button svg {
  transition: transform 0.2s ease;
}

.acoes button:hover svg {
  transform: scale(1.1);
}

@media (max-width: 1024px) {
  .grid {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }

  .cartao {
    padding: 16px;
  }

  .cartao.interno {
    padding: 14px;
  }
}

@media (max-width: 768px) {
  .acoes {
    flex-direction: column-reverse;
    gap: 12px;
  }

  .acoes button {
    width: 100%;
    min-width: auto;
  }

  .cartao-cabecalho {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .cartao-identificacao {
    align-items: flex-start;
  }

  .alternar-colapso {
    align-self: flex-start;
    font-size: 0.8rem;
  }

  .cartao-textos strong {
    font-size: 1rem;
  }
}

@media (max-width: 1024px) {
  .grid {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }

  .cartao {
    padding: 16px;
  }

  .cartao.interno {
    padding: 14px;
  }
}

@media (max-width: 768px) {
  .cartao-cabecalho {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .cartao-identificacao {
    align-items: flex-start;
  }

  .alternar-colapso {
    align-self: flex-start;
    font-size: 0.8rem;
  }

  .cartao-textos strong {
    font-size: 1rem;
  }
}

/* Estilos da Seção de Revisão */
.secao-revisao {
  background: transparent;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.secao-revisao .secao-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 0;
  border-bottom: 2px solid #e5e7eb;
}

.secao-revisao .header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.secao-revisao .header-content svg {
  color: #10b981;
  flex-shrink: 0;
}

.secao-revisao h3 {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 700;
  color: #1f2937;
}

.secao-revisao .header-content p {
  margin: 4px 0 0;
  color: #6b7280;
  font-size: 1rem;
}

.revisao-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 24px;
}

.revisao-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.revisao-card:hover {
  border-color: #d1d5db;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.revisao-card .card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f3f4f6;
}

.revisao-card .card-header svg {
  color: #3b82f6;
  flex-shrink: 0;
}

.revisao-card .card-header h4 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
}

.revisao-card .card-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-item .label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.info-item .value {
  font-size: 1rem;
  color: #1f2937;
  word-break: break-word;
}

.info-item .value.empty {
  color: #9ca3af;
  font-style: italic;
}

/* Badges da Revisão */
.revisao-card .badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.badge-planejamento {
  background: #dbeafe;
  color: #1d4ed8;
}

.badge-execucao {
  background: #fef3c7;
  color: #d97706;
}

.badge-concluida {
  background: #d1fae5;
  color: #065f46;
}

.badge-metrica {
  background: #f3e8ff;
  color: #7c3aed;
}

/* Tags */
.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag {
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  color: #475569;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
}

/* Equipe */
.equipe-lista {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.membro-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #f3f4f6;
}

.membro-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.membro-info strong {
  font-size: 0.9rem;
  color: #1f2937;
}

.membro-info span {
  font-size: 0.8rem;
  color: #6b7280;
}

.membro-alocacao {
  font-size: 0.875rem;
  font-weight: 600;
  color: #3b82f6;
  background: #dbeafe;
  padding: 4px 8px;
  border-radius: 4px;
}

.mais-membros {
  text-align: center;
  padding: 8px;
  color: #6b7280;
  font-size: 0.875rem;
  font-style: italic;
}

/* Orçamento */
.orcamento-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.orcamento-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.orcamento-item.full-width {
  grid-column: 1 / -1;
}

.montante {
  font-size: 1.25rem;
  font-weight: 700;
}

.montante.planejado {
  color: #059669;
}

.montante.executado {
  color: #dc2626;
}

.categorias-lista {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.categoria-tag {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  color: #0369a1;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.mais-categorias {
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 500;
  font-style: italic;
}

/* Métricas */
.metricas-lista {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.metrica-item {
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #f3f4f6;
}

.metrica-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.metrica-info strong {
  font-size: 0.9rem;
  color: #1f2937;
}

.metrica-valores {
  display: flex;
  gap: 16px;
  font-size: 0.8rem;
  color: #6b7280;
}

.mais-metricas {
  text-align: center;
  padding: 8px;
  color: #6b7280;
  font-size: 0.875rem;
  font-style: italic;
}

/* Estrutura do Projeto */
.estrutura-card {
  grid-column: 1 / -1;
}

.estrutura-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 20px;
}

.estrutura-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 20px;
  border-radius: 10px;
  transition: all 0.2s ease;
}

.estrutura-item.acao {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border: 1px solid #93c5fd;
}

.estrutura-item.entrega {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  border: 1px solid #6ee7b7;
}

.estrutura-item.tarefa {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 1px solid #f59e0b;
}

.estrutura-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.8);
}

.estrutura-item.acao .estrutura-icon {
  color: #1d4ed8;
}

.estrutura-item.entrega .estrutura-icon {
  color: #059669;
}

.estrutura-item.tarefa .estrutura-icon {
  color: #d97706;
}

.estrutura-info {
  text-align: center;
}

.estrutura-info .numero {
  display: block;
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
}

.estrutura-info .label {
  font-size: 0.9rem;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

/* Responsividade da Revisão */
@media (max-width: 1024px) {
  .revisao-container {
    grid-template-columns: 1fr;
  }

  .estrutura-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .secao-revisao .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .revisao-card {
    padding: 20px;
  }

  .orcamento-grid {
    grid-template-columns: 1fr;
  }

  .estrutura-grid {
    grid-template-columns: 1fr;
  }
}
</style>
